/*
╔══════════════════════════════════════════════════════════════════════════════════════════════╗
║                                                                                              ║
║  ████████╗ █████╗ ██╗     ███████╗███╗   ██╗████████╗███████╗██╗   ██╗███╗   ██╗███╗   ██╗   ║
║  ╚══██╔══╝██╔══██╗██║     ██╔════╝████╗  ██║╚══██╔══╝██╔════╝██║   ██║████╗  ██║████╗  ██║   ║
║     ██║   ███████║██║     █████╗  ██╔██╗ ██║   ██║   █████╗  ██║   ██║██╔██╗ ██║██╔██╗ ██║   ║
║     ██║   ██╔══██║██║     ██╔══╝  ██║╚██╗██║   ██║   ██╔══╝  ██║   ██║██║╚██╗██║██║╚██╗██║   ║
║     ██║   ██║  ██║███████╗███████╗██║ ╚████║   ██║   ██║     ╚██████╔╝██║ ╚████║██║ ╚████║   ║
║     ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝  ╚═══╝   ╚═╝   ╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═══╝   ║
║                                                                                              ║
║                              🌟 Finn ditt neste talent 🌟                                   ║
║                                                                                              ║
╚══════════════════════════════════════════════════════════════════════════════════════════════╝
*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, User, MessageSquare, Crown, Settings, Search, Ban, AlertTriangle, Briefcase, Bell } from 'lucide-react';
import { supabase } from './utils/supabase/client';
import { projectId, API_BASE, API_PUBLIC_BASE } from './utils/supabase/info';
import { fetchWithAuth } from './utils/api';
import { HomePage } from './components/HomePage';
import { GigsPage } from './components/GigsPage';
import { UserProfileView } from './components/UserProfileView';
import { MessagesPageEnhanced } from './components/MessagesPageEnhanced';
import { SubscriptionPage } from './components/SubscriptionPage';
import { GigsComingPage } from './components/GigsComingPage';
import { SubscriptionComingPage } from './components/SubscriptionComingPage';
import { StripeCheckout } from './components/StripeCheckout';
import { AuthPage } from './components/AuthPage';
import { FavoritesPage } from './components/FavoritesPage';
import { SavedSearchesPage } from './components/SavedSearchesPage';
import { AdminDashboard } from './components/AdminDashboard';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { UnsubscribePage } from './components/UnsubscribePage';
import { SettingsPage } from './components/SettingsPage';
import { FAQPage } from './components/FAQPage';
import { MyGigsPage } from './components/MyGigsPage';
import { MyGigInterestsPage } from './components/MyGigInterestsPage';
import { GigInterestsView } from './components/GigInterestsView';
import { DesktopSubPageModal } from './components/DesktopSubPageModal';
import { ThankYouPage } from './components/ThankYouPage';
import { WithdrawalFormPage } from './components/WithdrawalFormPage';
import { AccessGate } from './components/AccessGate';
import { AdminLogin } from './components/AdminLogin';
import { SubAdminLogin } from './components/SubAdminLogin';
import { SubAdminDashboard } from './components/SubAdminDashboard';
import { AboutPage } from './components/AboutPage';
import { TermsUpdateModal } from './components/TermsUpdateModal';
import { CookieConsent } from './components/CookieConsent';
import { AdminBlocker } from './components/AdminBlocker';
import { EmailVerificationPage } from './components/EmailVerificationPage';
import { NotificationBell } from './components/NotificationBell';
import { NotificationCenter } from './components/NotificationCenter';
import { CURRENT_TERMS_VERSION } from './utils/terms-version';
import { motion, AnimatePresence } from 'motion/react';
import { Footer } from './components/Footer';
import { Loader } from './components/ui/Loader';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { useSupabaseHealth } from './hooks/useSupabaseHealth';
import { isPushSubscribed, subscribeToPushNotifications } from './utils/pwa/pushNotifications';
import { Toaster, toast } from 'sonner';
import { AnnouncementModal } from './components/AnnouncementModal';
import { WhatIsTalentfunnModal } from './components/WhatIsTalentfunnModal';
import { devLog, devError, devWarn } from './utils/logger';
import { useModalFocus } from './utils/hooks/useModalFocus';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { createPortal } from 'react-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavProvider, useNav, type ViewType } from './contexts/NavContext';
import { ChatProvider, useChat } from './contexts/ChatContext';
import { WelcomeModalProvider, useWelcomeModal } from './contexts/WelcomeModalContext';
import { useUserSync } from './hooks/useUserSync';
import {
  clearSwUpdateGuard,
  consumeSwUpdatedFlag,
  hasSwUpdatedFlag,
  isSwUpdateGuardActive,
  markSwUpdated,
  markSwUpdating,
} from './utils/swUpdateState';
import { canCreateGig, canShowGigInterest, canUseFavorites, canUseSavedSearches, isUnauthenticatedViewAllowed } from './utils/accessPolicy';
import { triggerAndroidHaptic } from './utils/haptics';
import { trackFunnelEvent } from './utils/funnelTracking';

// Views that render inside the AnimatePresence wrapper (all other views use persistent mount/unmount)
const ANIMATED_VIEWS = new Set<ViewType>(['auth', 'checkout', 'admin', 'privacy', 'terms', 'forgot-password', 'unsubscribe', 'settings', 'faq', 'thank-you', 'angrerett', 'about']);

// Side-rekkefølge for animasjoner (slik de står i mobilmenyen fra venstre til høyre)
const PAGE_ORDER = ['home', 'profile', 'messages', 'gigs'];
const SCROLL_RESTORE_VIEWS = new Set<ViewType>([
  'home',
  'gigs',
  'gig-interests',
  'settings',
]);
/* De 4 sub-sidene (my-gigs, my-interests, favorites, saved-searches) er utelatt:
   vi vil alltid starte øverst når man går inn på dem (spesielt viktig på iOS PWA). */
/** On desktop (xl) these sub-pages render as modals over the previous view; on mobile they stay full-page. */
const SUB_PAGE_MODAL_VIEWS = new Set<ViewType>(['my-gigs', 'my-interests', 'favorites', 'saved-searches']);
const MAIN_VIEWS = new Set<ViewType>(['home', 'profile', 'messages', 'gigs']);

function getPageDirection(fromPage: string, toPage: string): 'left' | 'right' {
  const fromIndex = PAGE_ORDER.indexOf(fromPage);
  const toIndex = PAGE_ORDER.indexOf(toPage);
  
  // Hvis en av sidene ikke finnes i listen, default til 'left'
  if (fromIndex === -1 || toIndex === -1) return 'left';
  
  // Hvis vi går til høyre i listen, ny side skal komme fra høyre (direction = 'right')
  // Hvis vi går til venstre i listen, ny side skal komme fra venstre (direction = 'left')
  return toIndex > fromIndex ? 'right' : 'left';
}

// Animated page wrapper component
function AnimatedPage({ children, direction }: { children: React.ReactNode; direction: 'left' | 'right' }) {
  return (
    <motion.div
      className="h-full overflow-y-auto animated-page-scroll"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {children}
    </motion.div>
  );
}

function AppContent() {
  type HomeSearchPreset = {
    id: string;
    searchTerms: string[];
    counties: string[];
    name?: string;
  };
  type PendingPushNavigation = {
    destination: ViewType;
    savedSearchId?: string | null;
    gigId?: string | null;
    gigTitle?: string | null;
    recipientId?: string | null;
    senderId?: string | null;
    conversationId?: string | null;
    notificationType?: string | null;
  };

  const {
    currentView,
    setCurrentView,
    previousView,
    setPreviousView,
    selectedUserId,
    setSelectedUserId,
    messageRecipientId,
    setMessageRecipientId,
    navigateTo: navNavigateTo,
  } = useNav();
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState<string | null>(null); // For deep linking to saved search matches
  const [pendingPushNavigation, setPendingPushNavigation] = useState<PendingPushNavigation | null>(null);
  const [homeSearchPreset, setHomeSearchPreset] = useState<HomeSearchPreset | null>(null);
  const [interestsViewGigId, setInterestsViewGigId] = useState<string>(''); // For gig-interests view
  const [interestsViewGigTitle, setInterestsViewGigTitle] = useState<string>(''); // For gig-interests view
  const [createGigModalTrigger, setCreateGigModalTrigger] = useState<{ gigIdToEdit?: string; openInView?: 'home' | 'gigs' } | null>(null); // For triggering gig modals in HomePage or GigsPage
  const {
    session,
    setSession,
    userProfile,
    setUserProfile,
    isAdmin,
    setIsAdmin,
    isAdminCheckComplete,
    setIsAdminCheckComplete,
    blockedUserIds,
    setBlockedUserIds,
    handleSignOut: authHandleSignOut,
    fetchUserProfile: authFetchUserProfile,
  } = useAuth();
  const { unreadCount, refreshUnreadCount } = useChat();
  const [loading, setLoading] = useState(true);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [notificationBellAnchor, setNotificationBellAnchor] = useState<{
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<'plus' | 'employer' | 'employer_plus' | null>(null);
  const [needsTermsUpdate, setNeedsTermsUpdate] = useState(false);
  const [currentTermsVersion, setCurrentTermsVersion] = useState<string>('');
  const [isIosPwaLandscape, setIsIosPwaLandscape] = useState(false);
  
  // Admin portal mode (accessed via URL hash #admin)
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminLoginMode, setAdminLoginMode] = useState<'main' | 'sub'>('main');
  const [subAdminSession, setSubAdminSession] = useState<any>(null);
  const [adminVerifying, setAdminVerifying] = useState(false);
  const [adminVerifyError, setAdminVerifyError] = useState(false);
  
  // Email verification mode (accessed via URL hash #verify-email?token=xxx)
  const [isEmailVerificationMode, setIsEmailVerificationMode] = useState(false);
  
  // ✅ NEW: Subscription cancel modal state (moved from SubscriptionPage to fix display:none rendering bug)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelModalData, setCancelModalData] = useState<{
    subscriptionEndDate: string;
    currentPlan: string;
    onConfirm: () => void;
  } | null>(null);
  
  // ✅ NEW: Cancellation confirmation modal state
  const [showCancellationConfirmation, setShowCancellationConfirmation] = useState(false);
  const [cancellationDaysLeft, setCancellationDaysLeft] = useState<number>(0);
  
  // ✅ NEW: Reactivate modal state (moved from SubscriptionPage for consistency)
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  
  // ✅ NEW: Update banner state
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [updateWaiting, setUpdateWaiting] = useState<ServiceWorker | null>(null);
  const [isUpdating, setIsUpdating] = useState(false); // Loading state for update button
  const [reactivateModalData, setReactivateModalData] = useState<{
    currentPlan: string;
    onConfirm: () => void;
  } | null>(null);
  
  // ✅ NEW: Reactivation confirmation modal state
  const [showReactivationConfirmation, setShowReactivationConfirmation] = useState(false);
  
  // Pending hash view: after redirect (e.g. portal) we may land with #subscription/#settings before session is set; navigate when session is ready
  const [pendingHashView, setPendingHashView] = useState<ViewType | null>(null);
  
  // ✅ NEW: Modal action loading state (for async operations in modals)
  const [modalActionLoading, setModalActionLoading] = useState(false);
  
  // ⚠️ PILOT PHASE: Access gate state (REMOVE AT LAUNCH)
  const [hasAccess, setHasAccess] = useState(false);

  // "Hva er Talentfunn?" modal (shown when not logged in)
  const [showWhatIsTalentfunnModal, setShowWhatIsTalentfunnModal] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  // Checkout state
  const [checkoutPlanType, setCheckoutPlanType] = useState<'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus' | null>(null);
  
  // Announcement modal state
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<any>(null);
  const [checkoutPriceId, setCheckoutPriceId] = useState<string>('');
  const checkoutReturnToViewRef = useRef<'subscription' | 'settings' | null>(null);
  
  const { openWelcomeModal } = useWelcomeModal();

  useModalFocus(showCancelModal || showCancellationConfirmation || showReactivateModal || showReactivationConfirmation);
  
  // ✅ NEW: Track if this is the first login (to trigger welcome modal after email verification)
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  
  // Lagre direction i state for å unngå timing-problemer med AnimatePresence
  const [pageDirection, setPageDirection] = useState<'left' | 'right'>('left');

  // Desktop (xl) for smooth page transitions between main views
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1280px)');
    const update = () => setIsDesktop(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Access gate state
  const [accessGateEnabled, setAccessGateEnabled] = useState(false);
  const [accessGateTitle, setAccessGateTitle] = useState('');
  const [accessGateDescription, setAccessGateDescription] = useState('');
  const [accessGateButtonText, setAccessGateButtonText] = useState('');
  const [accessGateIcon, setAccessGateIcon] = useState('Lock');
  const [accessGateTheme, setAccessGateTheme] = useState<'default' | 'warning' | 'danger' | 'info' | 'success'>('default');
  const [accessGateAllowClose, setAccessGateAllowClose] = useState(true);
  const [accessGateShowButton, setAccessGateShowButton] = useState(true);
  const [accessGateShowInfo, setAccessGateShowInfo] = useState(true); // ✅ NEW - Toggle for info box
  const [accessGateSubtitle, setAccessGateSubtitle] = useState(''); // ✅ NEW
  const [accessGateInfoText, setAccessGateInfoText] = useState(''); // ✅ NEW
  const [accessGateFooterText, setAccessGateFooterText] = useState(''); // ✅ NEW
  const [accessGateContactEmail, setAccessGateContactEmail] = useState(''); // ✅ NEW

  // Supabase health monitoring
  const { healthStatus, checkHealth } = useSupabaseHealth();

  // Visit beacon: record one site visit per session (no auth, rate-limited on backend)
  useEffect(() => {
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (sessionStorage.getItem('talentfunn_visit_recorded') === '1') return;
      fetch(`${API_PUBLIC_BASE}/visit`, { method: 'POST', credentials: 'omit' })
        .then(() => sessionStorage.setItem('talentfunn_visit_recorded', '1'))
        .catch(() => {});
    } catch {
      // ignore
    }
  }, []);

  // Funnel tracking: user opened auth view (registration/login entry point).
  useEffect(() => {
    if (currentView !== 'auth') return;
    if (session) return;
    trackFunnelEvent('open_auth');
  }, [currentView, session]);

  // Show toast when database goes offline (only once per outage)
  const hasShownOfflineToast = useRef(false);
  
  // ✅ CRITICAL FIX: Ref to store Service Worker update interval ID for cleanup
  const swUpdateIntervalRef = useRef<number | null>(null);
  // Only show "økten utløpt" toast when we had a session (avoid spam from repeated SIGNED_OUT)
  const hadSessionBeforeSignOutRef = useRef(false);
  // Ref to main scroll container (for saving/restoring scroll on home and gigs)
  const mainRef = useRef<HTMLElement | null>(null);
  // Ref to current view for use in popstate (closure would be stale)
  const currentViewRef = useRef<ViewType>(currentView);
  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);
  // Ref to session for stable callbacks that should not depend on session closure
  const sessionRef = useRef<any>(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  // Keep SW update state in refs for event handlers/timeouts.
  const updateWaitingRef = useRef<ServiceWorker | null>(null);
  useEffect(() => {
    updateWaitingRef.current = updateWaiting;
  }, [updateWaiting]);
  const isUpdatingRef = useRef(false);
  useEffect(() => {
    isUpdatingRef.current = isUpdating;
  }, [isUpdating]);
  const userProfileRef = useRef<any>(userProfile);
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);
  const subscriptionRefetchRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    currentTermsVersionRef.current = currentTermsVersion;
  }, [currentTermsVersion]);
  useEffect(() => {
    needsTermsUpdateRef.current = needsTermsUpdate;
  }, [needsTermsUpdate]);
  const unreadChatCountRef = useRef<number>(unreadCount);
  useEffect(() => {
    unreadChatCountRef.current = unreadCount;
  }, [unreadCount]);
  const unreadNotificationCountRef = useRef<number>(unreadNotificationCount);
  useEffect(() => {
    unreadNotificationCountRef.current = unreadNotificationCount;
  }, [unreadNotificationCount]);
  const lastSignedOutToastAtRef = useRef(0);
  // Suppress green success toasts (e.g. "Databasen tilbake", "Oppdatert") shortly after session-expired toast
  const suppressSuccessToastsUntilRef = useRef(0);
  // Defer "Talentfunn er oppdatert" until session is known (only show if user stayed logged in)
  const pendingSwUpdatedToastRef = useRef(false);
  // Prevent duplicate "E-postadressen din er oppdatert!" toast when effect runs twice (e.g. Strict Mode)
  const emailChangeToastShownRef = useRef(false);
  // Prevent concurrent signOut calls (prevents toast spam on Android PWA reload)
  const isSigningOutRef = useRef(false);
  // Prevent multiple "konto ikke lenger aktiv" toasts when several fetchUserProfile calls get 401 at once
  const handlingProfile401Ref = useRef(false);
  // Deduplicate first-login welcome modal checks per user id
  const welcomeModalsCheckedForUserRef = useRef<string | null>(null);
  // Guard: keep bottom nav hidden while login is completing (prevents flash before auth page exits)
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const isAuthenticatingRef = useRef(false);
  useEffect(() => {
    isAuthenticatingRef.current = isAuthenticating;
  }, [isAuthenticating]);
  const [isModalFocusActive, setIsModalFocusActive] = useState(false);
  const isModalFocusActiveRef = useRef(false);
  // Logout overlay: fade out/in so transition to home is smooth (user-initiated logout only)
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const lastAuthProfileRefreshRef = useRef<{
    userId: string;
    event: 'INIT_BOOTSTRAP' | 'INITIAL_SESSION' | 'SIGNED_IN' | 'TOKEN_REFRESHED';
    at: number;
  } | null>(null);
  const profileRefreshInFlightRef = useRef<{
    userId: string | null;
    promise: Promise<any>;
  } | null>(null);
  const lastProfileRefreshAtRef = useRef(0);
  const currentTermsVersionRef = useRef<string>('');
  const needsTermsUpdateRef = useRef(false);
  const pendingTermsAcceptedVersionRef = useRef<string | null>(null);
  const pendingTermsAcceptanceUntilRef = useRef(0);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const notificationRefreshTimerRef = useRef<number | null>(null);
  const lastNotificationRefreshAtRef = useRef(0);
  const unreadNotificationFetchInFlightRef = useRef(false);
  const refreshUnreadCountRef = useRef(refreshUnreadCount);
  useEffect(() => {
    refreshUnreadCountRef.current = refreshUnreadCount;
  }, [refreshUnreadCount]);

  // iOS PWA: portal nav to body so fixed is visible; Android/desktop: nav in app tree
  // ✅ CRITICAL: Declare these BEFORE useEffect that uses them
  const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const isAndroidPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('android-pwa');

  // Ensure html has .ios when we detect iOS by userAgent but index.html didn't set it (e.g. cache, load order)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const hasIosClass = document.documentElement.classList.contains('ios') || document.documentElement.classList.contains('ios-pwa');
    const isIosByUA = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIosByUA && !hasIosClass) {
      document.documentElement.classList.add('ios');
    }
  }, []);

  useEffect(() => {
    accessTokenRef.current = session?.access_token ?? null;
  }, [session]);

  // iOS PWA production safety mode:
  // - Show a rotate-back overlay in landscape
  // - Force one clean re-init when returning to portrait after a rotation
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !isIosPwa) return;

    const ROTATED_KEY = 'talentfunn_ios_pwa_rotated';
    const RELOADED_KEY = 'talentfunn_ios_pwa_reloaded_after_rotate';

    const isLandscapeNow = () =>
      window.matchMedia('(orientation: landscape)').matches || window.innerWidth > window.innerHeight;

    const applyRotationState = () => {
      const landscape = isLandscapeNow();
      setIsIosPwaLandscape(landscape);

      if (landscape) {
        sessionStorage.setItem(ROTATED_KEY, '1');
        sessionStorage.removeItem(RELOADED_KEY);
        return;
      }

      const hadRotated = sessionStorage.getItem(ROTATED_KEY) === '1';
      const hasReloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
      if (hadRotated && !hasReloaded) {
        sessionStorage.setItem(RELOADED_KEY, '1');
        sessionStorage.removeItem(ROTATED_KEY);
        window.location.reload();
      }
    };

    applyRotationState();
    window.addEventListener('orientationchange', applyRotationState);
    window.addEventListener('resize', applyRotationState);
    window.addEventListener('pageshow', applyRotationState);

    return () => {
      window.removeEventListener('orientationchange', applyRotationState);
      window.removeEventListener('resize', applyRotationState);
      window.removeEventListener('pageshow', applyRotationState);
    };
  }, [isIosPwa]);

  useEffect(() => {
    const onModalFocusChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ isOpen?: boolean }>;
      setIsModalFocusActive(!!customEvent.detail?.isOpen);
    };
    window.addEventListener('talentfunn:modal-focus-changed', onModalFocusChanged as EventListener);
    return () => {
      window.removeEventListener('talentfunn:modal-focus-changed', onModalFocusChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    isModalFocusActiveRef.current = isModalFocusActive;
  }, [isModalFocusActive]);

  // When modal/overlay is active we set aria-hidden on nav; move focus out of nav to avoid "aria-hidden on focused element" warning.
  useEffect(() => {
    if (!isModalFocusActive || typeof document === 'undefined') return;
    const activeEl = document.activeElement as HTMLElement | null;
    if (!activeEl) return;
    const navTop = document.querySelector('.pwa-nav-top');
    const navBottom = document.querySelector('.pwa-bottom-nav');
    const isInNav = (navTop && navTop.contains(activeEl)) || (navBottom && navBottom.contains(activeEl));
    if (isInNav && mainRef.current) {
      mainRef.current.focus({ preventScroll: true });
    }
  }, [isModalFocusActive]);

  // Demp Supabase Auth-advarsel "SessionStorage is disabled" app-wide (kontekst kan begrense lagring, f.eks. i iframe/checkout).
  useEffect(() => {
    const origWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('SessionStorage is disabled')) return;
      origWarn.apply(console, args);
    };
    return () => {
      console.warn = origWarn;
    };
  }, []);

  // iOS PWA: paint top-level surfaces for Home/Oppdrag/Auth so overscroll bounce
  // uses page-colored background instead of white. Auth uses its own gradient (purple-100/pink-100/orange-100).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const htmlEl = document.documentElement;
    const homeGigs = isIosPwa && !isModalFocusActive && (currentView === 'home' || currentView === 'gigs' || currentView === 'gigs-coming');
    // Reuse the same "auth surface" class for iOS Safari too so overscroll/rubber-band
    // matches the AuthPage gradient (purple-100/pink-100/orange-100).
    const mode = htmlEl.getAttribute('data-tf-mode');
    const isIosSafari = mode === 'ios-safari';
    const auth = (isIosPwa || isIosSafari) && !isModalFocusActive && currentView === 'auth';
    htmlEl.classList.toggle('ios-pwa-home-gigs-surface', homeGigs);
    htmlEl.classList.toggle('ios-pwa-auth-surface', auth);
    return () => {
      htmlEl.classList.remove('ios-pwa-home-gigs-surface', 'ios-pwa-auth-surface');
    };
  }, [isIosPwa, isModalFocusActive, currentView]);

  // ✅ CRITICAL FIX: Helper to manage localStorage arrays with max size limit
  const addToLocalStorageArray = (key: string, id: string, maxEntries: number = 100) => {
    const existingArray = JSON.parse(localStorage.getItem(key) || '[]');
    
    // Only add if not already present
    if (!existingArray.includes(id)) {
      existingArray.push(id);
    }
    
    // Keep only last maxEntries to prevent unbounded growth
    const trimmedArray = existingArray.slice(-maxEntries);
    localStorage.setItem(key, JSON.stringify(trimmedArray));
  };
  
  useEffect(() => {
    if (!healthStatus.isOnline && !hasShownOfflineToast.current) {
      toast.error('Databasen er midlertidig utilgjengelig. Vi jobber med å løse problemet.', {
        duration: Infinity, // Vises til brukeren lukker den
        action: {
          label: 'Prøv igjen',
          onClick: () => {
            checkHealth();
          },
        },
      });
      hasShownOfflineToast.current = true;
    }
    
    // Reset when back online (don't show green toast if we just showed session-expired)
    if (healthStatus.isOnline && hasShownOfflineToast.current) {
      if (Date.now() < suppressSuccessToastsUntilRef.current) return;
      toast.success('Databasen er tilbake online!');
      hasShownOfflineToast.current = false;
    }
  }, [healthStatus.isOnline, checkHealth]);

  // ✅ NEW: Extract checkWelcomeModals as a reusable function
  // trigger: 'on_landing' = when user lands on page; 'after_login' = when user has just logged in (e.g. first login)
  const checkWelcomeModals = useCallback(async (trigger: 'on_landing' | 'after_login') => {
    try {
      // Check if user has visited before
      const hasVisited = localStorage.getItem('talentfunn_visited');
      
      // Fetch active welcome modals
      const response = await fetch(
        `${API_PUBLIC_BASE}/welcome-modals/active`
      );
      
      // If endpoint doesn't exist yet (404/500) or fetch fails (Figma Make), silently skip
      if (!response.ok) {
        return; // Silent fail - no error logging
      }
      
      const data = await response.json();
      const allModals = data.modals || [];
      // Only consider modals that match this trigger (showWhen). Legacy modals without showWhen = on_landing
      const modals = allModals.filter((m: { showWhen?: string }) => (m.showWhen ?? 'on_landing') === trigger);
      
      if (modals.length === 0) {
        return;
      }
      
      // Get already shown modals from localStorage
      const shownModals = JSON.parse(localStorage.getItem('talentfunn_welcome_modals_shown') || '[]');
      
      // Find first modal that should be shown
      for (const modal of modals) {
        // Check trigger type
        if (modal.triggerType === 'first_visit') {
          // Only show if user hasn't visited before AND modal hasn't been shown
          if (!hasVisited && !shownModals.includes(modal.id)) {
            openWelcomeModal(modal);
            break;
          }
        }
        
        if (modal.triggerType === 'every_visit') {
          // Show at most once per calendar day (within time range, backend already filtered)
          const today = new Date().toISOString().slice(0, 10);
          const shownByDate = JSON.parse(localStorage.getItem('talentfunn_every_visit_shown_dates') || '{}');
          if (shownByDate[modal.id] === today) break;
          shownByDate[modal.id] = today;
          try {
            localStorage.setItem('talentfunn_every_visit_shown_dates', JSON.stringify(shownByDate));
          } catch {
            /* ignore */
          }
          openWelcomeModal(modal);
          break; // Show only one modal at a time
        }
        
        if (modal.triggerType === 'first_visit_timed') {
          // Show only if NOT already shown (backend already filtered by time)
          if (!shownModals.includes(modal.id)) {
            openWelcomeModal(modal);
            break;
          }
        }
      }
    } catch (error) {
      // Silent fail in Figma Make (no Supabase connection)
      // In production (Netlify), this will work normally
      return;
    }
  }, [openWelcomeModal]);

  // Check for active welcome modals on mount (runs BEFORE pilot phase check) – only "on_landing" modals
  useEffect(() => {
    checkWelcomeModals('on_landing');
  }, [checkWelcomeModals]);

  // ✅ NEW: Trigger welcome modal when user logs in for the first time (after email verification) – only "after_login" modals
  useEffect(() => {
    const profileUserId = userProfile?.id ?? null;
    if (isFirstLogin && profileUserId) {
      if (welcomeModalsCheckedForUserRef.current === profileUserId) {
        return;
      }
      devLog('🎉 First login detected - checking welcome modals...');
      checkWelcomeModals('after_login');
      welcomeModalsCheckedForUserRef.current = profileUserId;
      setIsFirstLogin(false);
    }
  }, [isFirstLogin, userProfile?.id, checkWelcomeModals]);

  // ⚠️ PILOT PHASE: Check for existing access on mount (REMOVE AT LAUNCH)
  // ✅ MOVED: Access check moved into fetchSystemSettings to avoid race condition
  // (Must check accessGateEnabled FIRST before deciding if hasAccess matters)

  // ✅ NEW: Check for pending email verification on mount
  useEffect(() => {
    const checkPendingVerification = async () => {
      // Only check if user is NOT logged in
      if (session) return;
      
      const pendingEmail = localStorage.getItem('pendingVerificationEmail');
      
      if (pendingEmail) {
        devLog('✅ Found pending verification for:', pendingEmail);
        devLog('⚠️ Re-showing email verification modal on return');
        
        // Navigate to auth page and let AuthPage handle showing the modal
        // The modal will be shown automatically because pendingVerificationEmail exists in localStorage
        setCurrentView('auth');
      }
    };
    
    checkPendingVerification();
  }, []); // Run only once on mount

  // Check for admin portal access via URL hash (#admin)
  useEffect(() => {
    const checkAdminHash = async () => {
      // Check both #admin and #admin/ (with trailing slash)
      const hash = window.location.hash.toLowerCase();
      devLog('🔍 Checking admin hash:', hash);
      
      // Check for email verification FIRST (highest priority)
      if (hash.startsWith('#verify-email')) {
        devLog('✅ Email verification mode activated!');
        setIsEmailVerificationMode(true);
        return; // Exit early
      }
      
      // Check for password reset first (before regular navigation)
      if (hash.includes('reset-password') || (hash.includes('token=') && !hash.includes('access_token='))) {
        devLog('🔍 Password reset link detected - navigating to forgot-password page');
        devLog('📋 Full hash:', hash);
        setCurrentView('forgot-password');
        // Don't clear hash - ForgotPasswordPage needs to read the token
        return; // Exit early
      }
      
      // Check for regular navigation hash (NEW: Support #subscription, #settings, etc.)
      if (hash && hash !== '#' && hash !== '#admin' && hash !== '#admin/' && hash !== '#admin-login' && hash !== '#admin-login/' && !hash.includes('type=') && !hash.includes('access_token=')) {
        const cleanHash = hash.substring(1).split('/')[0]; // Remove # and any trailing /
        const validViews: ViewType[] = [
          'home', 'profile', 'messages', 'gigs', 'gigs-coming', 'subscription', 'subscription-coming', 'checkout', 'favorites',
          'saved-searches', 'auth', 'privacy', 'terms', 'forgot-password', 'settings', 
          'faq', 'thank-you', 'angrerett', 'about', 'my-gigs', 'my-interests', 'gig-interests'
        ];
        const hashToView = (h: string): ViewType => (h === 'subscription' ? 'subscription-coming' : h === 'gigs' ? 'gigs-coming' : h as ViewType);
        const targetView = hashToView(cleanHash);
        
        if (validViews.includes(targetView)) {
          const needsAuth = !sessionRef.current && !isUnauthenticatedViewAllowed(targetView);
          const safeTargetView = needsAuth ? 'home' : targetView;
          if (needsAuth && (targetView === 'subscription' || targetView === 'settings')) {
            setPendingHashView(targetView);
          }
          devLog('✅ Navigating to hash destination:', safeTargetView);
          setCurrentView(safeTargetView);
          // Clear hash after navigation
          window.history.replaceState(null, '', window.location.pathname);
          return; // Exit early
        }
      }
      
      if (hash === '#admin' || hash === '#admin/' || hash === '#admin-login' || hash === '#admin-login/') {
        const wantsSubAdmin = hash === '#admin-login' || hash === '#admin-login/';
        // Backward compatibility: keep legacy link working, but standardize URL entrypoint.
        if (wantsSubAdmin) {
          window.history.replaceState(null, '', `${window.location.pathname}#admin`);
        }

        devLog('✅ Admin mode activated!');
        setIsAdminMode(true);
        setAdminLoginMode(wantsSubAdmin ? 'sub' : 'main');
        setAdminVerifyError(false);
        
        // If user is already logged in, check if they're admin
        if (session) {
          devLog('🔍 User already logged in - checking admin status...');
          setAdminVerifying(true);
          try {
            const response = await fetch(
              `${API_BASE}/user-profile`,
              {
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                },
              }
            );

            if (response.ok) {
              const profile = await response.json();
              if (profile.isAdmin) {
                devLog('✅ User is admin - navigating to admin dashboard');
                setIsAdmin(true);
                setUserProfile(profile);
                setAdminVerifyError(false);
                window.location.hash = '';
                setIsAdminMode(false);
                setCurrentView('admin');
              } else {
                devLog('❌ User is NOT admin - logging out and showing admin login');
                await supabase.auth.signOut();
                setSession(null);
                setUserProfile(null);
                setIsAdmin(false);
              }
            } else {
              devLog('❌ Admin verify: profile fetch not ok', response.status);
              setAdminVerifyError(true);
            }
          } catch (err) {
            devError('Admin verify: profile fetch failed', err);
            setAdminVerifyError(true);
          } finally {
            setAdminVerifying(false);
          }
        }
      } else {
        setIsAdminMode(false);
        setAdminLoginMode('main');
        setAdminVerifyError(false);
      }

      // Check if sub-admin session exists in localStorage (works for unified #admin portal too)
      const storedSession = localStorage.getItem('sub_admin_session');
      if (storedSession) {
        try {
          const parsedSession = JSON.parse(storedSession);
          if (new Date(parsedSession.expiresAt) > new Date()) {
            setSubAdminSession(parsedSession);
          } else {
            localStorage.removeItem('sub_admin_session');
            setSubAdminSession(null);
          }
        } catch (e) {
          devError('Error parsing sub-admin session:', e);
          localStorage.removeItem('sub_admin_session');
          setSubAdminSession(null);
        }
      } else {
        setSubAdminSession(null);
      }
    };

    // Check on mount
    checkAdminHash();

    // Listen for hash changes
    window.addEventListener('hashchange', checkAdminHash);
    return () => window.removeEventListener('hashchange', checkAdminHash);
  }, [session]);

  // When session becomes available after redirect (e.g. portal return), navigate to pending hash view if we stored one
  useEffect(() => {
    if (session && pendingHashView && (pendingHashView === 'subscription' || pendingHashView === 'settings') && currentView === 'home') {
      setCurrentView(pendingHashView);
      setPendingHashView(null);
    }
  }, [session, pendingHashView, currentView]);

  // Fetch and subscribe to system settings (maintenance mode + access gate)
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const response = await fetch(
          `${API_PUBLIC_BASE}/maintenance-mode`
        );
        
        // Silently fail if endpoint doesn't exist yet (backend not deployed)
        if (!response.ok) {
          devLog('System settings endpoint not available yet (backend not deployed)');
          return;
        }
        
        const data = await response.json();
        if (data) {
          // Maintenance mode
          setMaintenanceMode(data.maintenanceMode || false);
          setMaintenanceMessage(data.maintenanceMessage || '');
          
          // Access gate
          const isAccessGateEnabled = data.accessGateEnabled || false;
          setAccessGateEnabled(isAccessGateEnabled);
          setAccessGateTitle(data.accessGateTitle || 'Pilot-fase'); // ✅ Changed: Pilot-mode default
          setAccessGateDescription(data.accessGateDescription || 'Skriv inn tilgangskode'); // ✅ Changed: Pilot-mode default
          setAccessGateButtonText(data.accessGateButtonText || 'Få tilgang'); // ✅ Changed: Pilot-mode default
          setAccessGateIcon(data.accessGateIcon || 'Lock');
          setAccessGateTheme(data.accessGateTheme || 'default');
          setAccessGateAllowClose(data.accessGateAllowClose !== undefined ? data.accessGateAllowClose : true);
          setAccessGateShowButton(data.accessGateShowButton !== undefined ? data.accessGateShowButton : true);
          setAccessGateShowInfo(data.accessGateShowInfo !== undefined ? data.accessGateShowInfo : true); // ✅ NEW - Toggle for info box
          setAccessGateSubtitle(data.accessGateSubtitle || 'Beta-tilgang påkrevd'); // ✅ NEW
          setAccessGateInfoText(data.accessGateInfoText || '<strong>Pilot-fase:</strong> Talentfunn er for øyeblikket i en lukket beta-fase. Hvis du har fått tilgang til pilot-programmet, bruk tilgangskoden du mottok.'); // ✅ NEW
          setAccessGateFooterText(data.accessGateFooterText || 'Vil du bli pilot-bruker?'); // ✅ NEW
          setAccessGateContactEmail(data.accessGateContactEmail || 'kontakt@talentfunn.no?subject=Pilot-program'); // ✅ NEW
          
          // ✅ NEW: Check localStorage AFTER we know if Access Gate is enabled
          // If Access Gate is disabled, grant access immediately
          // If enabled, check if user has passed it before
          if (!isAccessGateEnabled) {
            setHasAccess(true); // ✅ Access Gate disabled → Everyone gets in
          } else {
            const accessGranted = localStorage.getItem('talentfunn_access_granted');
            setHasAccess(accessGranted === 'true'); // ✅ Access Gate enabled → Check localStorage
          }
        }
      } catch (error) {
        // Silently fail - system settings are optional features
        devLog('System settings features not available yet');
        // ✅ NEW: If backend fails, allow access (fail-open for better UX)
        setHasAccess(true);
      } finally {
        // ✅ NEW: Mark checking as complete
        setCheckingAccess(false);
      }
    };

    // Initial fetch
    fetchSystemSettings();

    // ✅ Poll system settings every 60 seconds (replaces Realtime)
    const settingsInterval = setInterval(() => {
      fetchSystemSettings();
    }, 60000);

    return () => {
      clearInterval(settingsInterval);
    };
  }, []);

  // Admin login handler
  const handleAdminLogin = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(error.message);
    }

    if (data.session) {
      // ✅ CRITICAL: Verify email is confirmed before allowing admin login
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        await supabase.auth.signOut();
        throw new Error('Kunne ikke verifisere brukerstatus.');
      }

      if (!userData.user.email_confirmed_at) {
        await supabase.auth.signOut();
        throw new Error('E-post må bekreftes før du kan logge inn. Sjekk innboksen din.');
      }

      setSession(data.session);
      
      // Verify user is actually an admin
      const response = await fetch(
        `${API_BASE}/user-profile`,
        {
          headers: {
            Authorization: `Bearer ${data.session.access_token}`,
          },
        }
      );

      if (response.ok) {
        const profile = await response.json();
        if (profile.isAdmin) {
          setIsAdmin(true);
          setUserProfile(profile);
          setAdminVerifyError(false);
          // Remove #admin from URL and go to admin dashboard
          window.location.hash = '';
          setIsAdminMode(false);
          setCurrentView('admin');
        } else {
          // Not an admin - sign out and show error
          await supabase.auth.signOut();
          setSession(null);
          throw new Error('Du har ikke administratortilgang');
        }
      } else {
        setAdminVerifyError(true);
        throw new Error('Kunne ikke verifisere tilgang. Prøv igjen.');
      }
    }
  };

  const handleBackToNormalLogin = () => {
    window.location.hash = '';
    setIsAdminMode(false);
    setAdminLoginMode('main');
    setAdminVerifyError(false);
  };

  // Defer "oppdatert" toast: clear SW flags on mount; restore session from backup if needed
  useEffect(() => {
    const justUpdated = consumeSwUpdatedFlag();
    const wasUpdating = isSwUpdateGuardActive();

    if (justUpdated || wasUpdating) {
      // 🔒 CRITICAL: If main session was lost during SW update, restore from backup
      const mainSession = localStorage.getItem('talentfunn-auth');
      const backupSession = localStorage.getItem('talentfunn-auth-backup');
      if (!mainSession && backupSession) {
        devLog('🔄 Main session lost during SW update — restoring from backup');
        localStorage.setItem('talentfunn-auth', backupSession);
        // Reload so Supabase client picks up the restored session
        localStorage.removeItem('talentfunn-auth-backup');
        window.location.reload();
        return;
      }

      // Clean up backup (no longer needed)
      localStorage.removeItem('talentfunn-auth-backup');

      if (justUpdated) {
        pendingSwUpdatedToastRef.current = true;
      }
    }
  }, []);

  // 🔒 Global AbortError suppression during SW updates
  // Supabase internally uses navigator.locks which throw AbortErrors when SW swaps.
  // These unhandled rejections can propagate and corrupt auth state. Suppress them.
  useEffect(() => {
    const handler = (e: PromiseRejectionEvent) => {
      const isSwUpdating = isSwUpdateGuardActive();
      if (!isSwUpdating) return; // Only suppress during SW updates

      const reason = e.reason;
      if (reason instanceof DOMException && reason.name === 'AbortError') {
        devLog('🤫 Suppressed AbortError during SW update:', reason.message);
        e.preventDefault(); // Prevent it from showing in console and propagating
      } else if (reason instanceof Error && reason.message?.includes('aborted')) {
        devLog('🤫 Suppressed abort-related error during SW update:', reason.message);
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  useEffect(() => {
    if (!loading) {
      if (session && pendingSwUpdatedToastRef.current && Date.now() >= suppressSuccessToastsUntilRef.current) {
        pendingSwUpdatedToastRef.current = false;
        toast.success('Appen har blitt oppdatert', { duration: 3000 });
      } else if (!session) {
        pendingSwUpdatedToastRef.current = false;
      }
    }
  }, [loading, session]);

  // Keep SW guard active through app bootstrap, then clear.
  useEffect(() => {
    if (loading) return;
    const timer = window.setTimeout(() => {
      clearSwUpdateGuard();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [loading]);

  // iOS PWA only: set theme-color to match app background so overscroll/rubber-band area uses same color
  useEffect(() => {
    if (typeof document === 'undefined' || !document.documentElement.classList.contains('ios-pwa')) return;
    const applyThemeColor = () => {
      const bg = getComputedStyle(document.documentElement).getPropertyValue('--background').trim();
      if (!bg) return;
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]#theme-color-ios-pwa');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        meta.id = 'theme-color-ios-pwa';
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', bg);
    };
    applyThemeColor();
    const observer = new MutationObserver(applyThemeColor);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // PWA auto-update policy (B): when a new SW is waiting, auto-apply it the next
  // time the app goes to background. Avoids interrupting users mid-action, but
  // ensures security fixes ship quickly.
  const queueWaitingWorker = useCallback((worker: ServiceWorker, reason: string) => {
    if (!worker) return;
    // Always hide any legacy banner UI; we don't prompt anymore.
    if (showUpdateBanner) setShowUpdateBanner(false);
    if (updateWaitingRef.current === worker) return;
    devLog(`🆕 Queued waiting Service Worker (${reason})`);
    updateWaitingRef.current = worker;
    setUpdateWaiting(worker);
  }, [showUpdateBanner]);

  const autoApplyWaitingWorkerIfBackground = useCallback(async (reason: string) => {
    if (!('serviceWorker' in navigator)) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    if (isUpdatingRef.current) return;

    const worker = updateWaitingRef.current;
    if (!worker) return;

    // Mark update guard early so auth/session logic ignores transient AbortErrors.
    markSwUpdating();

    isUpdatingRef.current = true;
    setIsUpdating(true);

    devLog(`🔄 Auto-applying waiting Service Worker (background) (${reason})...`);

    try {
      // Best-effort session preservation before SW swap. This is intentionally
      // non-fatal; controllerchange handler will also back up right before reload.
      const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
      try {
        const currentSession = await supabase.auth.getSession();
        if (currentSession.data.session) {
          try {
            await supabase.auth.refreshSession();
          } catch (e) {
            devWarn('⚠️ Pre-update refreshSession failed (continuing):', e);
          }
          if (isAndroid) {
            await new Promise((r) => setTimeout(r, 500));
          }
          const authBackup = localStorage.getItem('talentfunn-auth');
          if (authBackup) localStorage.setItem('talentfunn-auth-backup', authBackup);
        }
      } catch (e) {
        devWarn('⚠️ Pre-update session backup failed (continuing):', e);
      }

      const allRegistrations = await navigator.serviceWorker.getRegistrations();
      const waitingWorkers: ServiceWorker[] = [];
      for (const reg of allRegistrations) {
        if (reg.waiting) waitingWorkers.push(reg.waiting);
      }
      if (!waitingWorkers.includes(worker)) waitingWorkers.push(worker);

      // Tell all waiting workers to activate. controllerchange will reload.
      for (const w of waitingWorkers) {
        w.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (error) {
      devWarn('⚠️ Auto-update failed to apply waiting worker:', error);
      // Allow retry later.
      isUpdatingRef.current = false;
      setIsUpdating(false);
      clearSwUpdateGuard();
    }
  }, []);

  // When app goes to background and an update is waiting, auto-apply it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void autoApplyWaitingWorkerIfBackground('visibility:hidden');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [autoApplyWaitingWorkerIfBackground]);

  // PWA: Check manifest and attempt Service Worker registration
  useEffect(() => {
    // Skip manifest check - it's injected dynamically via main.tsx
    devLog('📱 PWA Manifest injected dynamically');
    
    // Service Worker registration
    if ('serviceWorker' in navigator) {
      devLog('📱 Service Worker support detected');
      
      // Detect if we're in Figma Make or local development
      const isFigmaMake = window.location.hostname.includes('figma');
      const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.endsWith('.local');
      
      if (isFigmaMake) {
        // Skip Service Worker registration in Figma Make
        devLog('🎨 Figma Make detected - Service Worker registration skipped');
        devLog('💡 Service Worker will be active when deployed to Netlify');
        devLog('✅ PWA Manifest and icons are still available for testing');
      } else if (isLocalhost) {
        // Local dev should not run production SW logic (can cause stale cache/auth side effects).
        devLog('🧪 Localhost detected - skipping Service Worker registration');
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      } else {
        // Use /sw.js for production (Netlify)
        devLog('🚀 Production detected - registering Service Worker from /sw.js');
        
        // Use stable URL so we don't create a new "script" on every load (Date.now() caused
        // infinite "Oppdater" loop on Android: each reload registered a new URL → new waiting worker).
        // Updates are triggered by registration.update() which re-fetches /sw.js (updateViaCache: 'none').
        const swUrl = '/sw.js';
        
        navigator.serviceWorker.register(swUrl, { 
          scope: '/',
          updateViaCache: 'none' // CRITICAL: Never cache sw.js
        })
          .then(async (registration) => {
            const safeRegistrationUpdate = async (reason: string) => {
              try {
                await registration.update();
              } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                devWarn(`⚠️ Service Worker update check failed (${reason}):`, message);
              }
            };

            devLog('✅ Service Worker registered successfully!');
            devLog('✅ SW scope:', registration.scope);
            setSwRegistration(registration);
            
            // ✅ CRITICAL: Check if there's already a waiting service worker from a previous session
            // This handles the case where user didn't click "update" and closed the app
            // IMPORTANT: Check ALL registrations, not just the first one (Android may have multiple)
            const allRegistrations = await navigator.serviceWorker.getRegistrations();
            const waitingWorkers: ServiceWorker[] = [];
            
            // Check all registrations for waiting workers
            for (const reg of allRegistrations) {
              if (reg.waiting) {
                waitingWorkers.push(reg.waiting);
              }
            }
            
            // Also check the current registration
            if (registration.waiting && !waitingWorkers.includes(registration.waiting)) {
              waitingWorkers.push(registration.waiting);
            }
            
            if (waitingWorkers.length > 0) {
              devLog(`⚠️ Found ${waitingWorkers.length} existing waiting service worker(s) from previous session`);
              // Auto-update policy: queue and apply when app goes to background.
              queueWaitingWorker(waitingWorkers[0], 'register:existing-waiting');
              void autoApplyWaitingWorkerIfBackground('register:existing-waiting');
            }
            
            // Check if we have a very old SW that needs force-refresh
            if (registration.active) {
              // Get SW script URL to check version
              const activeWorker = registration.active;
              devLog('🔍 Active SW detected, checking version...');
              
              // Force immediate update check
              devLog('🔄 Forcing immediate update check...');
              await safeRegistrationUpdate('initial-force-check');
            }
            
            // Check for SW updates immediately on registration
            // ✅ CRITICAL: Don't check for updates if one is already in progress
            if (!isSwUpdateGuardActive()) {
              devLog('🔍 Checking for Service Worker updates immediately...');
              void safeRegistrationUpdate('post-register-check');
            } else {
              devLog('⚠️ Update in progress - skipping immediate update check');
            }
            
            // Handle SW updates automatically
            let handlingCurrentUpdateFound = false;
            registration.addEventListener('updatefound', async () => {
              // ✅ CRITICAL: Check if update is already in progress to prevent infinite loop
              if (isSwUpdateGuardActive()) {
                devLog('⚠️ Update already in progress - ignoring updatefound event to prevent loop');
                return;
              }
              handlingCurrentUpdateFound = true;
              
              // 🔒 EARLY FLAG: Set sw_updating as soon as a new SW is found.
              // Supabase navigator.locks can abort during SW install/activate (before controllerchange),
              // triggering spurious SIGNED_OUT events. This flag prevents session loss.
              markSwUpdating();
              devLog('🔒 Set sw_updating flag early (updatefound)');
              
              // Backup session immediately in case SIGNED_OUT fires before controllerchange
              const authData = localStorage.getItem('talentfunn-auth');
              if (authData) {
                localStorage.setItem('talentfunn-auth-backup', authData);
                devLog('✅ Session backed up early (updatefound)');
              }
              
              const newWorker = registration.installing;
              devLog('🆕 New Service Worker found, installing...', newWorker);
              
              if (newWorker) {
                newWorker.addEventListener('statechange', async () => {
                  devLog('🔄 SW state:', newWorker.state);
                  
                  // ✅ CRITICAL: Skip only if another update flow is active.
                  // The current updatefound flow itself sets the SW guard early on purpose.
                  if (isSwUpdateGuardActive() && !handlingCurrentUpdateFound) {
                    devLog('⚠️ Update in progress - skipping statechange handling');
                    return;
                  }
                  
                  if (newWorker.state === 'installed') {
                    devLog('✅ SW installed. Has controller?', !!navigator.serviceWorker.controller);
                    
                    if (navigator.serviceWorker.controller) {
                      // Check if there are multiple waiting service workers (multiple updates queued)
                      // If so, automatically skip all and reload to get to the latest version
                      const allRegistrations = await navigator.serviceWorker.getRegistrations();
                      const waitingWorkers: ServiceWorker[] = [];
                      
                      // Collect all waiting workers from all registrations
                      for (const reg of allRegistrations) {
                        if (reg.waiting) {
                          waitingWorkers.push(reg.waiting);
                        }
                      }
                      
                      // Also check if the current newWorker is now waiting
                      if (registration.waiting && !waitingWorkers.includes(registration.waiting)) {
                        waitingWorkers.push(registration.waiting);
                      }
                      
                      // If newWorker is installed but not yet in waitingWorkers, add it
                      if (newWorker.state === 'installed' && !waitingWorkers.includes(newWorker)) {
                        waitingWorkers.push(newWorker);
                      }
                      
                      devLog(`📊 Found ${waitingWorkers.length} waiting service worker(s)`);
                      
                      // Auto-update policy: queue and apply when app goes to background.
                      queueWaitingWorker(newWorker, 'updatefound:installed');
                      void autoApplyWaitingWorkerIfBackground('updatefound:installed');
                    }
                  }
                  
                  if (newWorker.state === 'activated') {
                    devLog('🎉 New SW activated!');
                    handlingCurrentUpdateFound = false;
                    
                    // Only reload if there's actually a new SW controlling the page
                    if (!navigator.serviceWorker.controller) {
                      devLog('🔄 Reloading to use new SW...');
                      window.location.reload();
                    }
                  }
                });
              }
            });
            
            // Listen for controlling SW changes and reload with session preservation
            // ✅ BULLETPROOF: Don't attempt network calls (refreshSession) here — network is
            //    often unavailable during SW activation on Android, causing AbortErrors and
            //    session loss. Instead just verify localStorage has the session, then reload.
            navigator.serviceWorker.addEventListener('controllerchange', async () => {
              // 🔒 IMMEDIATELY flag that an update is in progress so auth handlers
              // (onAuthStateChange, refreshSession, AuthPage) ignore AbortErrors.
              // Use BOTH sessionStorage (for same-page checks) and localStorage (survives app restart).
              markSwUpdating();
              
              devLog('🔄 SW controller changed - preserving session before reload...');
              
              const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
              
              try {
                // Step 1: Backup session to a separate key that our SIGNED_OUT handler won't touch
                const authInStorage = typeof localStorage !== 'undefined' && localStorage.getItem('talentfunn-auth');
                if (authInStorage) {
                  localStorage.setItem('talentfunn-auth-backup', authInStorage);
                  devLog('✅ Session backed up to talentfunn-auth-backup before reload');
                } else {
                  devLog('⚠️ No session in localStorage before reload - user may need to login');
                }
                
                // Step 2: Small delay for Android storage sync, then reload
                const waitTime = isAndroid ? 600 : 200;
                devLog(`⏳ Waiting ${waitTime}ms for storage sync...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              } catch (err) {
                devError('⚠️ Error during pre-reload check:', err);
              } finally {
                markSwUpdated();
                devLog('🔄 Reloading page now...');
                window.location.reload();
              }
            });
            
            // Listen for messages from Service Worker (e.g., notification clicks)
            navigator.serviceWorker.addEventListener('message', (event) => {
              if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                devLog('📬 Notification click message received:', event.data);
                
                // ✅ PRIORITY 1: Handle external custom URLs (open in new tab)
                if (event.data.url && event.data.url.startsWith('http')) {
                  devLog('🌐 Opening external URL in new tab:', event.data.url);
                  window.open(event.data.url, '_blank');
                  return;
                }
                
                const normalizedDestination =
                  event.data.destination === 'explore' ? 'home' : event.data.destination;
                const destinationAsView = normalizedDestination as ViewType | undefined;
                const isSavedSearchNavigation =
                  event.data.url === '/saved-searches' || normalizedDestination === 'saved-searches';

                // Cold-start safety: if auth/profile is not ready yet, queue protected push navigation.
                // This prevents fallback routing to home during startup races.
                if (
                  isSavedSearchNavigation &&
                  (!sessionRef.current || (sessionRef.current && !userProfileRef.current))
                ) {
                  setPendingPushNavigation({
                    destination: 'saved-searches',
                    savedSearchId: event.data.savedSearchId ?? null,
                    notificationType: event.data.notificationType ?? null,
                  });
                  return;
                }
                if (
                  destinationAsView &&
                  !isUnauthenticatedViewAllowed(destinationAsView) &&
                  !sessionRef.current
                ) {
                  setPendingPushNavigation({
                    destination: destinationAsView,
                    savedSearchId: event.data.savedSearchId ?? null,
                    gigId: event.data.gigId ?? null,
                    gigTitle: event.data.gigTitle ?? null,
                    recipientId: event.data.recipientId ?? null,
                    senderId: event.data.senderId ?? null,
                    conversationId: event.data.conversationId ?? null,
                    notificationType: event.data.notificationType ?? null,
                  });
                  return;
                }

                // Handle saved searches notification with deep link to matches
                if (
                  isSavedSearchNavigation &&
                  event.data.savedSearchId
                ) {
                  devLog('🔗 Deep linking to saved search:', event.data.savedSearchId);
                  setSelectedSavedSearchId(event.data.savedSearchId);
                  navigateTo('saved-searches');
                } else if (event.data.url === '/saved-searches' || normalizedDestination === 'saved-searches') {
                  // Saved searches without specific ID
                  setSelectedSavedSearchId(null);
                  navigateTo('saved-searches');
                } else if (event.data.notificationType === 'gig_interest' && event.data.gigId) {
                  setInterestsViewGigId(event.data.gigId);
                  if (event.data.gigTitle) setInterestsViewGigTitle(event.data.gigTitle);
                  navigateTo('gig-interests');
                } else if (normalizedDestination === 'messages') {
                  // Handle message notifications (recipientId preferred, conversationId fallback)
                  const immediateRecipientId = event.data.recipientId || event.data.senderId;
                  if (immediateRecipientId) {
                    navigateTo('messages', undefined, immediateRecipientId);
                    return;
                  }

                  if (!event.data.conversationId || !sessionRef.current) {
                    navigateTo('messages');
                    return;
                  }

                  void (async () => {
                    try {
                      const response = await fetchWithAuth(`${API_BASE}/conversations`, { cache: 'no-store' });
                      if (!response.ok) {
                        navigateTo('messages');
                        return;
                      }
                      const data = await response.json();
                      const conversation = (data.conversations || []).find((conv: any) => conv.id === event.data.conversationId);
                      if (conversation?.userId) {
                        navigateTo('messages', undefined, conversation.userId);
                        return;
                      }
                    } catch (error) {
                      devWarn('Could not resolve conversation recipient from notification:', error);
                    }
                    navigateTo('messages');
                  })();
                } else if (normalizedDestination) {
                  // Handle generic destination
                  navigateTo(normalizedDestination);
                }
              } else if (event.data && event.data.type === 'SHOW_ANNOUNCEMENT') {
                // Handle announcement modal from push notification click
                devLog('📢 Announcement notification clicked:', event.data.announcementId);
                fetchAndShowAnnouncement(event.data.announcementId);
              } else if (event.data && event.data.type === 'GET_UNREAD_COUNT') {
                // ✅ Service Worker requesting unread count for badge update
                const totalUnread = unreadChatCountRef.current + unreadNotificationCountRef.current;
                if (navigator.setAppBadge && totalUnread > 0) {
                  navigator.setAppBadge(totalUnread);
                  devLog('✅ App badge updated to:', totalUnread);
                } else if (navigator.setAppBadge) {
                  navigator.clearAppBadge();
                  devLog('✅ App badge cleared');
                }
              }
            });
            
            // Check for updates every 30 seconds
            swUpdateIntervalRef.current = window.setInterval(() => {
              devLog('🔍 Periodic update check...');
              void safeRegistrationUpdate('periodic-30s');
            }, 30 * 1000); // 30 seconds
          })
          .catch(error => {
            devWarn('⚠️ Service Worker registration failed:', error.message);
            devLog('💡 If you see this in production, check that /sw.js exists in /public folder');
          });
      }
    } else {
      devWarn('⚠️ Service Worker not supported in this browser');
    }
    
    // ✅ CRITICAL FIX: Cleanup Service Worker update interval on unmount
    return () => {
      if (swUpdateIntervalRef.current !== null) {
        clearInterval(swUpdateIntervalRef.current);
        swUpdateIntervalRef.current = null;
      }
    };
  }, []);

  // Keep app icon badge synced with unread state while app is open.
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    const totalUnread = unreadCount + unreadNotificationCount;
    if (totalUnread > 0) {
      navigator.setAppBadge(totalUnread);
    } else {
      navigator.clearAppBadge();
    }
  }, [unreadCount, unreadNotificationCount]);

  // Fallback: on some Android PWA flows, updatefound/statechange can be missed.
  // Periodically look for a waiting worker so we can auto-apply on background.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const surfaceWaitingWorker = async (reason: string) => {
      if (isUpdatingRef.current) return;
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const waiting = registrations.find((reg) => reg.waiting)?.waiting ?? null;
        if (waiting) {
          devLog(`✅ Found waiting worker via fallback (${reason})`);
          queueWaitingWorker(waiting, `fallback:${reason}`);
          void autoApplyWaitingWorkerIfBackground(`fallback:${reason}`);
        }
      } catch (error) {
        devWarn('⚠️ Waiting worker fallback check failed:', error);
      }
    };

    void surfaceWaitingWorker('mount');

    const onFocus = () => void surfaceWaitingWorker('focus');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void surfaceWaitingWorker('visible');
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(() => {
      void surfaceWaitingWorker('interval');
    }, 30000);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [queueWaitingWorker, autoApplyWaitingWorkerIfBackground]);

  // Save scroll position for list/settings views so we can restore when user returns.
  // iOS PWA can occasionally route scroll to document instead of <main>, so store the max.
  const saveScrollForView = useCallback((view: ViewType) => {
    if (!SCROLL_RESTORE_VIEWS.has(view)) return;
    const mainScrollTop = mainRef.current?.scrollTop ?? 0;
    const docScrollTop =
      typeof document !== 'undefined' ? (document.scrollingElement?.scrollTop ?? 0) : 0;
    sessionStorage.setItem('talentfunn_scroll_' + view, String(Math.max(mainScrollTop, docScrollTop)));
  }, []);

  // Navigation helper (keeps ViewType centralized). Gigs → coming-soon; subscription → subscription coming-soon (SubscriptionComingPage).
  const navigateTo = useCallback(
    (view: ViewType, userId?: string, recipientId?: string) => {
      let targetView: ViewType = view;
      if (view === 'subscription' || view === 'subscription-coming') targetView = 'subscription-coming';
      else if (view === 'gigs') targetView = 'gigs-coming';
      navNavigateTo(targetView, {
        userId,
        recipientId,
        currentSession: sessionRef.current,
        saveScrollForView,
        setPageDirection,
      });
    },
    [navNavigateTo, saveScrollForView]
  );

  // 🔒 Global guard: if auth session disappears while user is on a protected view,
  // always snap back to Home. This prevents getting "stuck" on pages like Settings
  // with hidden bottom nav but ongoing authed polling (401 spam).
  useEffect(() => {
    if (session) return;
    if (isUnauthenticatedViewAllowed(currentView)) return;
    devLog('🔒 Session missing on protected view — redirecting to home:', currentView);
    setSelectedUserId(null);
    setMessageRecipientId(null);
    navigateTo('home');
  }, [session, currentView, navigateTo, setSelectedUserId, setMessageRecipientId]);

  const getHistoryFromView = useCallback((): ViewType | null => {
    const fromView = window.history.state?.fromView;
    return typeof fromView === 'string' ? (fromView as ViewType) : null;
  }, []);

  const isSubPageModalActive = isDesktop && session && SUB_PAGE_MODAL_VIEWS.has(currentView);
  const rawBackView = getHistoryFromView() || previousView || 'profile';
  const mainContentView: ViewType = isSubPageModalActive
    ? (MAIN_VIEWS.has(rawBackView as ViewType) ? rawBackView : 'profile') as ViewType
    : currentView;
  const handleSubPageModalBack = useCallback(() => {
    const origin = getHistoryFromView() || previousView || 'profile';
    if (origin === 'profile' && session?.user?.id) {
      navigateTo('profile', session.user.id);
      return;
    }
    if (origin === 'home' || origin === 'gigs' || origin === 'gigs-coming' || origin === 'messages') {
      navigateTo(origin);
      return;
    }
    navigateTo('profile', session?.user?.id ?? '');
  }, [getHistoryFromView, previousView, session, navigateTo]);

  // 🔒 Guard: redirect away from my-gigs if user doesn't have employer_plus
  useEffect(() => {
    if (currentView === 'my-gigs' && !canCreateGig(userProfile)) {
      navigateTo('home');
    }
  }, [currentView, userProfile]);

  // 🔒 Guard premium-only subpages against direct URL/history access.
  useEffect(() => {
    // Avoid redirect races during startup or while profile is still loading.
    if (loading) return;
    if (session && !userProfile) return;
    if (currentView === 'favorites' && !canUseFavorites(userProfile)) {
      navigateTo('subscription');
      return;
    }
    if (currentView === 'saved-searches' && !canUseSavedSearches(userProfile)) {
      navigateTo('subscription');
      return;
    }
    if (currentView === 'my-interests' && !canShowGigInterest(userProfile)) {
      navigateTo('subscription');
    }
  }, [currentView, loading, session, userProfile, navigateTo]);

  useEffect(() => {
    if (!pendingPushNavigation) return;
    if (loading) return;

    const destination = pendingPushNavigation.destination;
    const requiresAuth = !isUnauthenticatedViewAllowed(destination);

    if (requiresAuth && !session) {
      setPendingPushNavigation(null);
      navigateTo('auth');
      return;
    }

    if (destination === 'saved-searches') {
      if (!userProfile) return;
      if (!canUseSavedSearches(userProfile)) {
        setPendingPushNavigation(null);
        navigateTo('subscription');
        return;
      }
      setSelectedSavedSearchId(pendingPushNavigation.savedSearchId || null);
      setPendingPushNavigation(null);
      navigateTo('saved-searches');
      return;
    }

    if (destination === 'gig-interests') {
      if (pendingPushNavigation.gigId) {
        setInterestsViewGigId(pendingPushNavigation.gigId);
      }
      if (pendingPushNavigation.gigTitle) {
        setInterestsViewGigTitle(pendingPushNavigation.gigTitle);
      }
      setPendingPushNavigation(null);
      navigateTo('gig-interests');
      return;
    }

    if (destination === 'messages') {
      const recipientId = pendingPushNavigation.recipientId || pendingPushNavigation.senderId || undefined;
      setPendingPushNavigation(null);
      if (recipientId) {
        navigateTo('messages', undefined, recipientId);
      } else {
        navigateTo('messages');
      }
      return;
    }

    setPendingPushNavigation(null);
    navigateTo(destination);
  }, [pendingPushNavigation, loading, session, userProfile, navigateTo]);

  // Guard profile view against missing user id (can happen from direct URL/back-state).
  useEffect(() => {
    if (currentView !== 'profile' || selectedUserId) return;
    if (session?.user?.id) {
      setSelectedUserId(session.user.id);
    } else {
      navigateTo('home');
    }
  }, [currentView, selectedUserId, session?.user?.id, navigateTo]);

  // Scroll: restore saved position for selected views, otherwise scroll to top
  useEffect(() => {
    const isIosPwaNow =
      typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    const scrollMainAndMaybeDocument = (y: number, behavior: ScrollBehavior = 'auto') => {
      const mainEl = mainRef.current ?? document.querySelector('main');
      if (mainEl) {
        mainEl.scrollTo({ top: y, behavior });
      }
      if (isIosPwaNow) {
        window.scrollTo({ top: y, behavior });
      }
    };

    if (SCROLL_RESTORE_VIEWS.has(currentView)) {
      const key = 'talentfunn_scroll_' + currentView;
      const saved = sessionStorage.getItem(key);
      if (saved !== null) {
        const y = parseInt(saved, 10);
        if (!isNaN(y) && y >= 0) {
          sessionStorage.removeItem(key);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // iOS PWA: use auto to avoid smooth-scroll lockups in standalone WebKit.
              scrollMainAndMaybeDocument(y, isIosPwaNow ? 'auto' : 'smooth');
            });
          });
          return;
        }
      }
    }
    scrollMainAndMaybeDocument(0, isIosPwaNow ? 'auto' : 'smooth');
    if (!isIosPwaNow) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  // Guard: if we're on checkout view without plan/priceId (e.g. reload or direct link), leave checkout once
  useEffect(() => {
    if (currentView === 'checkout' && session && (!checkoutPlanType || !checkoutPriceId)) {
      setCheckoutPlanType(null);
      setCheckoutPriceId('');
      navigateTo('subscription');
    }
  }, [currentView, session, checkoutPlanType, checkoutPriceId, navigateTo]);

  // Guard: thank-you requires subscriptionType (set after successful payment). Redirect if missing (e.g. refresh/direct link).
  useEffect(() => {
    if (currentView === 'thank-you' && !subscriptionType) {
      navigateTo('subscription');
    }
  }, [currentView, subscriptionType, navigateTo]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const validViews: ViewType[] = [
      'home', 'profile', 'messages', 'gigs', 'gigs-coming', 'subscription', 'subscription-coming', 'checkout', 'favorites',
      'saved-searches', 'auth', 'privacy', 'terms', 'forgot-password', 'settings',
      'faq', 'thank-you', 'angrerett', 'about', 'my-gigs', 'my-interests', 'gig-interests'
    ];
    const normalizeRedirectViews = (v: ViewType): ViewType => {
      if (v === 'subscription') return 'subscription-coming';
      if (v === 'gigs') return 'gigs-coming';
      return v;
    };
    const normalizeViewForAccess = (candidate: ViewType): ViewType => {
      if (!sessionRef.current && !isUnauthenticatedViewAllowed(candidate)) return 'home';
      return candidate;
    };
    const getViewFromUrl = (): ViewType => {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view') as ViewType | null;
      if (viewParam && validViews.includes(viewParam)) return normalizeViewForAccess(viewParam);
      const hash = window.location.hash.replace(/^#/, '').split('/')[0];
      if (hash) {
        const viewFromHash = normalizeRedirectViews((hash === 'subscription' ? 'subscription' : hash === 'gigs' ? 'gigs' : hash) as ViewType);
        if (validViews.includes(viewFromHash)) return normalizeViewForAccess(viewFromHash);
      }
      return 'home';
    };
    const consumePendingGigProfileReturn = (): string | null => {
      const raw = sessionStorage.getItem('talentfunn_pending_gig_profile_return');
      if (!raw) return null;
      sessionStorage.removeItem('talentfunn_pending_gig_profile_return');
      try {
        const parsed = JSON.parse(raw) as { gigId?: string; at?: number };
        if (!parsed?.gigId) return null;
        const isFresh = typeof parsed.at === 'number' && Date.now() - parsed.at < 5 * 60 * 1000;
        return isFresh ? parsed.gigId : null;
      } catch {
        return null;
      }
    };
    const restoreGigModalFromProfile = (fromView: ViewType): boolean => {
      if (fromView !== 'profile') return false;
      const pendingGigId = consumePendingGigProfileReturn();
      if (!pendingGigId) return false;
      const targetView: ViewType = 'gigs';
      const restoredDirection = getPageDirection(fromView, targetView);
      setPageDirection(restoredDirection);
      setPreviousView(fromView);
      setCurrentView(targetView);
      requestAnimationFrame(() => {
        window.dispatchEvent(
          new CustomEvent('talentfunn:open-gig-detail', {
            detail: { gigId: pendingGigId },
          })
        );
      });
      return true;
    };

    const handlePopState = (event: PopStateEvent) => {
      const fromView = currentViewRef.current;
      // Save scroll for selected views when leaving (current view from ref, closure would be stale)
      if (SCROLL_RESTORE_VIEWS.has(fromView)) {
        saveScrollForView(fromView);
      }
      // Modal history entries are handled by modal components.
      // If no modal is currently open, this is a stale modal entry (e.g. profile opened from gig modal)
      // and should restore the underlying page view from URL.
      if (event.state?.modal) {
        if (!isModalFocusActiveRef.current) {
          const modalType = event.state.modal as string | undefined;
          const modalGigId = typeof event.state.gigId === 'string' ? event.state.gigId : null;
          let restoredView = getViewFromUrl();
          if (restoredView === 'auth' && sessionRef.current) {
            restoredView = 'home';
          }
          if (restoredView !== fromView) {
            const restoredDirection = getPageDirection(fromView, restoredView);
            setPageDirection(restoredDirection);
            setPreviousView(fromView);
            setCurrentView(restoredView);
          }
          if (modalType === 'gig-detail' && modalGigId) {
            requestAnimationFrame(() => {
              window.dispatchEvent(
                new CustomEvent('talentfunn:open-gig-detail', {
                  detail: { gigId: modalGigId },
                })
              );
            });
          } else {
            void restoreGigModalFromProfile(fromView);
          }
        }
        return;
      }
      if (event.state) {
        let stateView = event.state.view as ViewType | undefined;
        if (stateView === 'subscription') stateView = 'subscription-coming';
        if (stateView === 'gigs') stateView = 'gigs-coming';
        const candidateView = stateView && validViews.includes(stateView) ? stateView : 'home';
        const newView = normalizeViewForAccess(candidateView);
        const reopenGigId = typeof event.state.reopenGigId === 'string' ? event.state.reopenGigId : null;
        if (restoreGigModalFromProfile(fromView)) {
          return;
        }
        const newDirection = getPageDirection(fromView, newView);
        setPageDirection(newDirection);
        setPreviousView(fromView);
        setCurrentView(newView);
        if (newView === 'profile') {
          const fallbackProfileId = sessionRef.current?.user?.id ?? null;
          setSelectedUserId(event.state.userId || fallbackProfileId);
        } else {
          setSelectedUserId(null);
        }
        if (event.state.recipientId) setMessageRecipientId(event.state.recipientId);
        if (newView === 'gigs' && reopenGigId) {
          requestAnimationFrame(() => {
            window.dispatchEvent(
              new CustomEvent('talentfunn:open-gig-detail', {
                detail: { gigId: reopenGigId },
              })
            );
          });
        }
      } else {
        // No state (e.g. after returning from Stripe, or first history entry) – restore view from URL
        if (restoreGigModalFromProfile(fromView)) {
          return;
        }
        let newView = getViewFromUrl();
        // In some browsers (e.g. Android) URL may not be updated yet when popstate fires; we'd get same view and "go nowhere"
        if (newView === fromView) {
          newView = (previousView && validViews.includes(previousView) ? previousView : 'home') as ViewType;
          newView = normalizeViewForAccess(newView);
        }
        if (newView === 'auth' && sessionRef.current) {
          newView = 'home';
        }
        const urlParams = new URLSearchParams(window.location.search);
        const newDirection = getPageDirection(fromView, newView);
        setPageDirection(newDirection);
        setPreviousView(fromView);
        setCurrentView(newView);
        if (newView === 'profile') {
          setSelectedUserId(urlParams.get('userId') || sessionRef.current?.user?.id || null);
        } else {
          setSelectedUserId(null);
        }
        if (newView === 'messages' && urlParams.get('recipientId')) setMessageRecipientId(urlParams.get('recipientId'));
        else if (newView !== 'messages') setMessageRecipientId(null);
        if (newView === 'saved-searches') {
          setSelectedSavedSearchId(urlParams.get('savedSearchId'));
        } else {
          setSelectedSavedSearchId(null);
        }
        if (newView === 'gig-interests') {
          const gigIdParam = urlParams.get('gigId');
          if (gigIdParam) setInterestsViewGigId(gigIdParam);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Initialize state from URL on mount
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view') as ViewType;
    const userIdParam = urlParams.get('userId');
    const recipientIdParam = urlParams.get('recipientId');
    const savedSearchIdParam = urlParams.get('savedSearchId');
    const gigIdParam = urlParams.get('gigId');
    
    // ✅ NEW: Support ?profile=userId format for shared profile links
    const profileIdParam = urlParams.get('profile');
    
    if (profileIdParam) {
      // Profile share link detected - navigate to profile view
      devLog('✅ Profile share link detected - userId:', profileIdParam);
      if (currentView === 'home' || currentView === 'gigs') {
        saveScrollForView(currentView);
      }
      const newDirection = getPageDirection(currentView, 'profile');
      setPageDirection(newDirection);
      setPreviousView(currentView);
      setCurrentView('profile');
      setSelectedUserId(profileIdParam);
      // Clean up URL (remove query params)
      window.history.replaceState(null, '', window.location.pathname);
    } else if (viewParam && viewParam !== 'home') {
      // Ensure a "home" history entry so device back from this view goes to home, not out of app.
      const currentSearch = window.location.search || '?view=home';
      const homeState = { view: 'home' as ViewType, fromView: currentViewRef.current };
      window.history.replaceState(homeState, '', window.location.pathname + '?view=home');
      const deepLinkState = {
        view: viewParam,
        userId: userIdParam ?? undefined,
        recipientId: recipientIdParam ?? undefined,
        fromView: 'home' as ViewType,
      };
      window.history.pushState(deepLinkState, '', window.location.pathname + currentSearch);

      if (currentView === 'home' || currentView === 'gigs') {
        saveScrollForView(currentView);
      }
      const newDirection = getPageDirection(currentView, viewParam);
      setPageDirection(newDirection);
      setPreviousView(currentView);
      setCurrentView(viewParam);
      if (userIdParam) setSelectedUserId(userIdParam);
      if (recipientIdParam) setMessageRecipientId(recipientIdParam);
      if (viewParam === 'saved-searches') {
        setSelectedSavedSearchId(savedSearchIdParam);
      }
      if (viewParam === 'gig-interests' && gigIdParam) {
        setInterestsViewGigId(gigIdParam);
      }
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, [saveScrollForView, previousView]);

  // Check for legacy payment URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const legacyStripeSessionId = urlParams.get('session_id');
    const legacyPaymentStatus = urlParams.get('payment');
    const legacyType = urlParams.get('type');

    // Legacy cleanup only. Payment verification now happens via embedded checkout callbacks.
    if (legacyStripeSessionId || legacyPaymentStatus) {
      window.history.replaceState({}, document.title, window.location.pathname);
      if (session && (legacyStripeSessionId || (legacyPaymentStatus === 'success' && (legacyType === 'plus' || legacyType === 'employer')))) {
        navigateTo('home');
      }
    }
  }, [session]); // ✅ Re-run when session changes

  // Use ref to prevent session fetch loop
  const isInitializing = useRef(true);

  const refreshUnreadNotificationCount = useCallback((reason: string = 'manual') => {
    if (!sessionRef.current) {
      setUnreadNotificationCount(0);
      return;
    }

    const now = Date.now();
    const elapsed = now - lastNotificationRefreshAtRef.current;
    const MIN_REFRESH_GAP_MS = 3000;
    const MIN_FORCED_REFRESH_GAP_MS = 1200;
    const isForcedRefreshReason =
      reason === 'notifications:read' ||
      reason === 'notifications:subscribed' ||
      reason === 'notifications:manual-force';

    const minGap = isForcedRefreshReason ? MIN_FORCED_REFRESH_GAP_MS : MIN_REFRESH_GAP_MS;
    if (elapsed < minGap) return;
    if (unreadNotificationFetchInFlightRef.current) return;

    if (notificationRefreshTimerRef.current !== null) {
      window.clearTimeout(notificationRefreshTimerRef.current);
      notificationRefreshTimerRef.current = null;
    }

    notificationRefreshTimerRef.current = window.setTimeout(async () => {
      try {
        unreadNotificationFetchInFlightRef.current = true;
        lastNotificationRefreshAtRef.current = Date.now();
        devLog('🔔 Fetching notification count:', reason);
        const response = await fetchWithAuth(
          `${API_BASE}/notifications/unread-count`
        );

        if (response.ok) {
          const data = await response.json();
          devLog('🔔 Notification count fetched:', data.unreadCount);
          setUnreadNotificationCount(data.unreadCount || 0);
        } else {
          devError('🔔 Failed to fetch notification count:', response.status);
        }
      } catch (error) {
        devError('Error fetching unread notification count:', error);
      } finally {
        unreadNotificationFetchInFlightRef.current = false;
        notificationRefreshTimerRef.current = null;
      }
    }, 200);
  }, []);

  // Update unread count when session changes
  useEffect(() => {
    let notificationChannel: any = null;
    let tokenRefreshInterval: any = null; // Move outside if block so cleanup can access it
    let notificationReconnectTimer: number | null = null;
    let notificationFallbackPollTimer: number | null = null;
    let notificationRetryAttempt = 0;
    let didCleanup = false;

    if (session) {
      refreshUnreadCountRef.current?.('session:start');
      refreshUnreadNotificationCount('session:start');
      const stopNotificationFallbackPolling = (reason: string) => {
        if (notificationFallbackPollTimer !== null) {
          window.clearInterval(notificationFallbackPollTimer);
          notificationFallbackPollTimer = null;
          devLog('✅ Notification unread fallback polling stopped:', reason);
        }
      };

      const startNotificationFallbackPolling = (reason: string) => {
        if (notificationFallbackPollTimer !== null) return;
        devWarn('⚠️ Notification unread realtime degraded - starting fallback polling:', reason);
        refreshUnreadNotificationCount(`notifications:fallback:${reason}`);
        notificationFallbackPollTimer = window.setInterval(() => {
          if (document.visibilityState === 'visible') {
            refreshUnreadNotificationCount('notifications:fallback:poll');
          }
        }, 30000);
      };

      const clearNotificationReconnectTimer = () => {
        if (notificationReconnectTimer !== null) {
          window.clearTimeout(notificationReconnectTimer);
          notificationReconnectTimer = null;
        }
      };

      const subscribeNotificationChannel = () => {
        if (didCleanup) return;
        if (notificationChannel) {
          supabase.removeChannel(notificationChannel);
          notificationChannel = null;
        }

        notificationChannel = supabase
          .channel(`notifications-unread:${session.user.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${session.user.id}`,
            },
            (payload) => {
              // Defer work so the realtime message handler returns immediately (avoids "handler took 169ms" violation).
              window.setTimeout(() => {
                refreshUnreadNotificationCount('notifications:*');
                const changedNotification = (payload as any)?.new;
                const isSavedSearchNotification =
                  changedNotification?.type === 'saved_search_match' ||
                  changedNotification?.destination === 'saved-searches';
                if (isSavedSearchNotification) {
                  window.dispatchEvent(new Event('talentfunn:saved-searches-updated'));
                }
              }, 0);
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              notificationRetryAttempt = 0;
              clearNotificationReconnectTimer();
              stopNotificationFallbackPolling('SUBSCRIBED');
              devLog('✅ Notification unread channel subscribed');
              refreshUnreadNotificationCount('notifications:subscribed');
              return;
            }

            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
              const reason =
                status === 'CHANNEL_ERROR'
                  ? 'CHANNEL_ERROR'
                  : status === 'TIMED_OUT'
                  ? 'TIMED_OUT'
                  : 'CLOSED';
              startNotificationFallbackPolling(reason);
              notificationRetryAttempt += 1;
              clearNotificationReconnectTimer();
              const retryDelay = Math.min(15000, 1200 * Math.max(1, notificationRetryAttempt));
              notificationReconnectTimer = window.setTimeout(() => {
                if (!didCleanup) {
                  devWarn(`🔄 Re-subscribing notification unread channel after ${reason} (attempt ${notificationRetryAttempt})`);
                  subscribeNotificationChannel();
                }
              }, retryDelay);
              if (status === 'CHANNEL_ERROR') {
                devWarn('❌ Notification unread channel error; retry scheduled');
              } else if (status === 'TIMED_OUT') {
                devLog('⏱️ Notification unread channel timed out; retry scheduled');
              } else {
                devWarn('🔒 Notification unread channel closed; retry scheduled');
              }
            }
          });
      };

      subscribeNotificationChannel();

      const handleVisibilityOrFocus = () => {
        if (document.visibilityState === 'visible') {
          refreshUnreadNotificationCount('app:visible');
        }
      };

      const handleOnline = () => {
        refreshUnreadNotificationCount('network:online');
      };

      document.addEventListener('visibilitychange', handleVisibilityOrFocus);
      window.addEventListener('focus', handleVisibilityOrFocus);
      window.addEventListener('online', handleOnline);

      // Proactive token refresh (tokens expire after 60 minutes).
      // Use jitter to avoid synchronized refresh spikes across many users.
      const runTokenRefresh = async () => {
        // Mobile browsers can have brief network/DNS hiccups even while the screen is on.
        // A transient refresh failure should NOT force a logout; only a definitely-invalid
        // refresh token should. (Supabase will keep the current session until it expires.)
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          devLog('🔄 Skipping proactive token refresh (offline)');
          return;
        }
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          devLog('🔄 Skipping proactive token refresh (tab not visible)');
          return;
        }

        devLog('🔄 Proactively refreshing token (periodic check)...');
        
        // ✅ CRITICAL FIX: Retry logic with exponential backoff (3 attempts)
        let refreshSuccess = false;
        let lastRefreshError: any = null;
        const maxRetries = 3;
        const retryDelays = [0, 2000, 5000]; // 0ms, 2s, 5s
        
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          if (attempt > 0) {
            devLog(`🔄 Retry attempt ${attempt}/${maxRetries - 1} after ${retryDelays[attempt]}ms...`);
            await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
          }
          
          const { data, error } = await supabase.auth.refreshSession();
          lastRefreshError = error;
          
          if (!error && data.session) {
            devLog('✅ Token refreshed proactively (periodic)');
            setSession(data.session);
            refreshSuccess = true;
            break; // Success - exit retry loop
          } else {
            if (attempt < maxRetries - 1) {
              devLog(`⚠️ Token refresh attempt ${attempt + 1}/${maxRetries} failed; will retry`, error);
            } else {
              devError(`❌ Failed to refresh token (attempt ${attempt + 1}/${maxRetries}):`, error);
            }
          }
        }
        
        // ✅ CRITICAL FIX: Only sign out if ALL retries failed
        // 🔒 But NOT during SW updates — AbortErrors are expected and session is safe in localStorage
        if (!refreshSuccess) {
          const isSwUpdating = isSwUpdateGuardActive();
          if (isSwUpdating) {
            devLog('🤫 Token refresh failed during SW update — skipping sign-out (session safe in localStorage)');
          } else {
            const message = (lastRefreshError?.message || '').toString().toLowerCase();
            const status = lastRefreshError?.status;
            const definitelyInvalidRefreshToken =
              status === 400 ||
              status === 401 ||
              message.includes('invalid refresh token') ||
              message.includes('refresh token not found') ||
              message.includes('token is expired') ||
              message.includes('jwt expired') ||
              message.includes('invalid token');

            if (definitelyInvalidRefreshToken) {
              devError('❌ Proactive refresh failed due to invalid refresh token - signing out...');
              hadSessionBeforeSignOutRef.current = false; // Avoid duplicate toast in onAuthStateChange
              // Toast removed: onAuthStateChange will show it when SIGNED_OUT event fires
              if (!isSigningOutRef.current) {
                isSigningOutRef.current = true;
                await supabase.auth.signOut();
                setTimeout(() => { isSigningOutRef.current = false; }, 2000);
              }
            } else {
              // Treat as transient: keep current session and try again on next tick.
              devWarn('⚠️ Proactive token refresh failed (transient) - preserving session', lastRefreshError);
            }
          }
        }
      };

      const scheduleNextTokenRefresh = () => {
        if (didCleanup) return;
        // Base: 50 minutes; Jitter: +/- 10 minutes.
        const baseMs = 50 * 60 * 1000;
        const jitterMs = (Math.random() * 20 - 10) * 60 * 1000;
        const nextDelayMs = Math.max(5 * 60 * 1000, Math.round(baseMs + jitterMs));
        tokenRefreshInterval = setTimeout(async () => {
          if (didCleanup) return;
          await runTokenRefresh();
          scheduleNextTokenRefresh();
        }, nextDelayMs);
      };

      scheduleNextTokenRefresh();

      // ✅ CRITICAL FIX: Always cleanup on unmount or session change
      const cleanup = () => {
        didCleanup = true;
        clearNotificationReconnectTimer();
        stopNotificationFallbackPolling('cleanup');
        if (notificationChannel) supabase.removeChannel(notificationChannel);
        document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
        window.removeEventListener('focus', handleVisibilityOrFocus);
        window.removeEventListener('online', handleOnline);
        if (notificationRefreshTimerRef.current !== null) {
          window.clearTimeout(notificationRefreshTimerRef.current);
          notificationRefreshTimerRef.current = null;
        }
        if (tokenRefreshInterval) clearTimeout(tokenRefreshInterval);
      };
      
      return cleanup;
    } else {
      setUnreadNotificationCount(0);
    }
  }, [session?.user?.id, refreshUnreadNotificationCount]);

  // ✅ FIX: Refresh unread count when entering messages view
  useEffect(() => {
    if (currentView === 'messages' && session) {
      refreshUnreadCount('view:messages');
    }
  }, [currentView, session, refreshUnreadCount]);

  // ✅ Sync profile/settings when opening Settings so changes from another device are visible
  useEffect(() => {
    if (currentView === 'settings' && session) {
      void requestUserProfileRefresh('view:settings');
    }
  }, [currentView, session]);

  useEffect(() => {
    if (!isInitializing.current) return; // Only run once
    isInitializing.current = false;
    
    // ✅ SAFETY NET: Timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        const swGuardActive = isSwUpdateGuardActive() || hasSwUpdatedFlag();
        devError('⚠️ App loading timeout - forcing UI render');
        setLoading(false);
        if (swGuardActive) {
          devLog('ℹ️ Suppressing loading-timeout toast during SW update guard window');
          return;
        }
        toast.error('Det tok lengre tid enn forventet å laste inn. Prøv å refresh siden.', {
          duration: 10000,
          action: {
            label: 'Refresh',
            onClick: () => window.location.reload(),
          },
        });
      }
    }, 10000); // 10 second timeout
    
    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      // ✅ PREVENTIVE TOKEN REFRESH: Refresh token on app start to ensure freshness
      if (session) {
        devLog('🔄 App started with session - preventively refreshing token...');
        
        // Validate session expiry before attempting refresh (prevents unnecessary failures on Android PWA)
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const isExpired = expiresAt && expiresAt < now;
        const expiresSoon = expiresAt && expiresAt < now + 300; // Less than 5 min remaining
        
        if (isExpired) {
          devLog('⚠️ Session already expired, signing out...');
          hadSessionBeforeSignOutRef.current = false; // No toast for already-expired session
          if (!isSigningOutRef.current) {
            isSigningOutRef.current = true;
            await supabase.auth.signOut();
            setTimeout(() => { isSigningOutRef.current = false; }, 2000);
          }
          setSession(null);
          setLoading(false);
          clearTimeout(loadingTimeout);
          return;
        }
        
        if (expiresSoon) {
          devLog('⚠️ Session expires soon, refreshing...');
        }
        
        // Check if this is after a service worker update (retry logic)
        const justUpdated = hasSwUpdatedFlag();
        const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
        
        // ✅ BULLETPROOF: After SW update, skip refreshSession() entirely.
        // Network is often flaky right after SW activation, and a failed refreshSession()
        // can trigger Supabase's onAuthStateChange with SIGNED_OUT, wiping the session.
        // Instead, trust the existing session and schedule a deferred refresh.
        if (justUpdated) {
          devLog('🔄 Post-update: using existing session directly (skipping refresh to avoid network errors)');
          setSession(session);
          // Deferred refresh: try after 10s when network should be stable
          setTimeout(async () => {
            try {
              const { data, error } = await supabase.auth.refreshSession();
              if (!error && data.session) {
                devLog('✅ Deferred token refresh succeeded after update');
              } else {
                devWarn('⚠️ Deferred token refresh failed after update (session still valid):', error?.message);
              }
            } catch (e) {
              devWarn('⚠️ Deferred token refresh exception (session still valid):', e);
            }
          }, 10000);
        } else {
          // Normal flow (not after update): refresh token immediately
          let refreshData, refreshError;
          try {
            ({ data: refreshData, error: refreshError } = await supabase.auth.refreshSession());
          } catch (error) {
            devError('❌ Exception during refresh:', error);
            refreshError = error as any;
          }
          
          if (refreshError) {
            devError('❌ Failed to refresh session on app start:', refreshError);
            if (session.expires_at && session.expires_at > now + 60) {
              devLog('⚠️ Using existing session despite refresh failure (session still valid)');
              setSession(session);
            } else {
              setSession(null);
            }
          } else if (refreshData && refreshData.session) {
            setSession(refreshData.session);
          } else {
            setSession(session);
          }
        }
        hadSessionBeforeSignOutRef.current = true;
        
        // Fetch user profile and announcements - wrap in try/catch to prevent app crash
        try {
          lastAuthProfileRefreshRef.current = {
            userId: session.user.id,
            event: 'INIT_BOOTSTRAP',
            at: Date.now(),
          };
          await fetchUserProfile();
        } catch (error) {
          devError('⚠️ Failed to fetch user profile on app start:', error);
          // Continue loading even if profile fetch fails - user can retry later
        }
        
        try {
          await checkActiveAnnouncement();
        } catch (error) {
          devError('⚠️ Failed to check active announcement on app start:', error);
          // Continue loading even if announcement check fails
        }
      } else {
        // No session found - check if this is after an update (might need retry)
        const justUpdated = hasSwUpdatedFlag();
        const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
        
        if (justUpdated) {
          // After SW update, localStorage/Supabase may be slow to initialize; retry getSession several times
          const delays = isAndroid ? [500, 1000, 2000, 3000] : [300, 800, 1500];
          for (let i = 0; i < delays.length; i++) {
            devLog(`⚠️ No session after update - retry ${i + 1}/${delays.length} in ${delays[i]}ms...`);
            await new Promise(resolve => setTimeout(resolve, delays[i]));
            
            // Try Supabase getSession first
            const retrySession = await supabase.auth.getSession();
            if (retrySession.data.session) {
              devLog('✅ Session found on retry after update');
              setSession(retrySession.data.session);
              hadSessionBeforeSignOutRef.current = true;
              try {
                lastAuthProfileRefreshRef.current = {
                  userId: retrySession.data.session.user.id,
                  event: 'INIT_BOOTSTRAP',
                  at: Date.now(),
                };
                await fetchUserProfile();
              } catch (error) {
                devError('⚠️ Failed to fetch user profile on retry after update:', error);
              }
              try {
                await checkActiveAnnouncement();
              } catch (error) {
                devError('⚠️ Failed to check active announcement on retry after update:', error);
              }
              setLoading(false);
              clearTimeout(loadingTimeout);
              return;
            }
            
            // Fallback: check if raw session data exists in localStorage (Supabase may not have initialized yet)
            if (isAndroid && i === delays.length - 1) {
              const rawAuth = localStorage.getItem('talentfunn-auth');
              if (rawAuth) {
                devLog('⚠️ Raw session found in localStorage but Supabase getSession failed - forcing refresh...');
                try {
                  const { data: refreshData } = await supabase.auth.refreshSession();
                  if (refreshData?.session) {
                    devLog('✅ Session restored via refresh from localStorage data');
                    setSession(refreshData.session);
                    hadSessionBeforeSignOutRef.current = true;
                    try {
                      lastAuthProfileRefreshRef.current = {
                        userId: refreshData.session.user.id,
                        event: 'INIT_BOOTSTRAP',
                        at: Date.now(),
                      };
                      await fetchUserProfile();
                    } catch (_) {}
                    try { await checkActiveAnnouncement(); } catch (_) {}
                    setLoading(false);
                    clearTimeout(loadingTimeout);
                    return;
                  }
                } catch (refreshErr) {
                  devError('❌ Refresh from localStorage data failed:', refreshErr);
                }
              }
            }
          }
          devLog('⚠️ Still no session after all retries - user will need to login');
        }
        setSession(null);
      }
      
      setLoading(false);
      clearTimeout(loadingTimeout); // Clear timeout on successful load
    }).catch((error) => {
      setLoading(false);
      clearTimeout(loadingTimeout);
      const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      const isAbortLike = message.includes('abort') || message.includes('aborted');
      const swGuardActive = isSwUpdateGuardActive() || hasSwUpdatedFlag();
      if (swGuardActive || isAbortLike) {
        devWarn('⚠️ Startup getSession failed during transient SW/auth state:', error);
      } else {
        devError('❌ Failed to get session:', error);
      }
      if (swGuardActive || isAbortLike) {
        devLog('ℹ️ Suppressing getSession startup error toast during transient SW/auth state');
        return;
      }
      toast.error('Kunne ikke laste inn brukerdata. Prøv å refresh siden.', {
        duration: 10000,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload(),
        },
      });
    });

    // Listen for auth changes (including token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      devLog('🔐 Auth state changed:', _event);
      // During a SW update, Supabase lock-signals may be aborted which can trigger a spurious
      // SIGNED_OUT. Ignore it unless the user explicitly clicked "logg ut".
      if (_event === 'SIGNED_OUT') {
        const isSwUpdating = isSwUpdateGuardActive();
        const isIntentionalLogout = isSigningOutRef.current;
        if (isSwUpdating && !isIntentionalLogout) {
          devLog('🤫 Ignoring SIGNED_OUT during SW update — session preserved in localStorage');
          return;
        }
      }

      setSession(session);
      // Keep ref in sync immediately to avoid racey navigate/guards in same tick.
      sessionRef.current = session;
      window.dispatchEvent(
        new CustomEvent('talentfunn:auth-session-changed', {
          detail: { event: _event, userId: session?.user?.id ?? null },
        })
      );

      if (session) hadSessionBeforeSignOutRef.current = true;

      // 🔒 SECURITY: Clear ALL user-specific state on logout to prevent data leakage between accounts
      if (_event === 'SIGNED_OUT') {
        const isIntentionalLogout = isSigningOutRef.current;
        if (isIntentionalLogout) {
          devLog('🚪 Intentional logout - clearing state');
        } else {
          devLog('🚪 User signed out - clearing all state...');
        }
        setUserProfile(null);
        setUnreadNotificationCount(0);
        setIsAdmin(false);
        setSubscriptionType(null);
        setCurrentTermsVersion('');
        setNeedsTermsUpdate(false);
        setBlockedUserIds([]);
        localStorage.removeItem('talentfunn_is_admin');
        localStorage.removeItem('pendingVerificationEmail');
        localStorage.removeItem('sub_admin_session');
        setSelectedUserId(null);
        setMessageRecipientId(null);
        setCheckoutPlanType(null);
        setCheckoutPriceId('');
        lastAuthProfileRefreshRef.current = null;
        profileRefreshInFlightRef.current = null;
        lastProfileRefreshAtRef.current = 0;
        // UX requirement: regardless of what caused logout, always land on Home.
        // (Unauthenticated users can browse Home + profiles.)
        navigateTo('home');

        if (!isIntentionalLogout) {
          const hadSession = hadSessionBeforeSignOutRef.current;
          hadSessionBeforeSignOutRef.current = false;
          const now = Date.now();
          if (hadSession && now - lastSignedOutToastAtRef.current > 15000) {
            lastSignedOutToastAtRef.current = now;
            suppressSuccessToastsUntilRef.current = now + 12000;
            toast.info('Økten har utløpt. Vennligst logg inn på nytt.', { duration: 5000 });
          }
        } else {
          hadSessionBeforeSignOutRef.current = false;
        }
        devLog('✅ All state cleared - user logged out');
      }

      if (_event === 'TOKEN_REFRESHED' && session) {
        devLog('✅ Token refreshed successfully');
        if (shouldSkipAuthProfileRefresh('TOKEN_REFRESHED', session)) {
          devLog('ℹ️ Skipping duplicate profile refresh for TOKEN_REFRESHED');
        } else {
          lastAuthProfileRefreshRef.current = {
            userId: session.user.id,
            event: 'TOKEN_REFRESHED',
            at: Date.now(),
          };
          void requestUserProfileRefresh('auth:TOKEN_REFRESHED', { sessionOverride: session }).catch((error) => {
            devWarn('Profile refresh failed after TOKEN_REFRESHED:', error);
          });
        }
      }
      if ((_event === 'SIGNED_IN' || _event === 'INITIAL_SESSION') && session) {
        if (_event === 'SIGNED_IN' && isAuthenticatingRef.current) {
          devLog('ℹ️ SIGNED_IN ignored while explicit login flow is running');
          return;
        }
        if (shouldSkipAuthProfileRefresh(_event, session)) {
          devLog(`ℹ️ Skipping duplicate profile refresh for ${_event}`);
        } else {
          lastAuthProfileRefreshRef.current = {
            userId: session.user.id,
            event: _event,
            at: Date.now(),
          };
          devLog('✅ User session detected - fetching profile...', _event);
          void requestUserProfileRefresh(`auth:${_event}`, { sessionOverride: session }).catch((error) => {
            devWarn(`Profile refresh failed after ${_event}:`, error);
          });
        }

        // Auto-navigate away from auth page ONLY on INITIAL_SESSION (app start / page reload).
        // NOT on SIGNED_IN — that fires during active login and would race with onAuthSuccess.
        if (_event === 'INITIAL_SESSION' && currentViewRef.current === 'auth') {
          devLog('🔄 Existing session found on app start while on auth page — redirecting to home');
          navigateTo('home');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check for #change-email hash (email confirmation success)
  useEffect(() => {
    const checkEmailChangeConfirmation = async () => {
      let hash = window.location.hash.substring(1); // Remove first #
      
      if (!hash) return;
      
      // Handle double hash case similar to password reset
      // Supabase might send: #change-email#access_token=XXX&type=email_change
      // Or: #email-change#access_token=XXX&type=email_change
      if (hash.includes('#')) {
        const secondHashIndex = hash.indexOf('#');
        const prefix = hash.substring(0, secondHashIndex);
        const tokenPart = hash.substring(secondHashIndex + 1);
        
        devLog('🔍 Email change hash detected:', { prefix, hasTokens: tokenPart.includes('access_token') });
        
        // If there are tokens after the second hash, parse them
        if (tokenPart.includes('access_token')) {
          const params = new URLSearchParams(tokenPart);
          const type = params.get('type');
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');
          
          devLog('📋 Email change tokens:', { type, hasAccessToken: !!access_token, hasRefreshToken: !!refresh_token });
          
          // If this is an email change confirmation with tokens
          if ((type === 'email_change' || type === 'signup') && access_token && refresh_token) {
            try {
              devLog('✅ Setting session for email change confirmation...');
              
              // Set the session using the tokens
              const { data, error } = await supabase.auth.setSession({
                access_token,
                refresh_token
              });
              
              if (error) {
                devError('❌ Failed to set session for email change:', error);
                toast.error('Kunne ikke bekrefte e-postendring. Vennligst prøv igjen.');
                window.history.replaceState(null, '', window.location.pathname);
                return;
              }
              
              devLog('✅ Session established for email change');
              if (!emailChangeToastShownRef.current) {
                emailChangeToastShownRef.current = true;
                toast.success('E-postadressen din er oppdatert!');
              }
              // Clean up URL
              window.history.replaceState(null, '', window.location.pathname);
              emailChangeToastShownRef.current = false;
              
              // Refresh user profile to get updated email
              if (session || data.session) {
                void requestUserProfileRefresh('email-change:token-confirm');
              }
            } catch (err) {
              devError('❌ Error confirming email change:', err);
              toast.error('Noe gikk galt. Vennligst prøv igjen.');
              window.history.replaceState(null, '', window.location.pathname);
            }
            return;
          }
        }
        
        // Use the prefix for simple hash check
        hash = prefix;
      }
      
      // Simple hash check for backwards compatibility
      // Supabase might also send just: #change-email (without tokens)
      if (hash === 'change-email' || hash === 'email-change') {
        devLog('✅ Simple email change confirmation detected');
        if (!emailChangeToastShownRef.current) {
          emailChangeToastShownRef.current = true;
          toast.success('E-postadressen din er oppdatert!');
        }
        // Clear hash
        window.history.replaceState(null, '', window.location.pathname);
        emailChangeToastShownRef.current = false;
        // Refresh user profile to get updated email
        if (session) {
          void requestUserProfileRefresh('email-change:simple-confirm');
        }
      }
    };
    
    checkEmailChangeConfirmation();
  }, [session]);

  const handleSignOut = useCallback(async () => {
    if (isLoggingOut) return;
    devLog('🚪 User initiated logout - starting fade transition');
    isSigningOutRef.current = true;
    setIsLoggingOut(true);
    try {
      await new Promise((r) => setTimeout(r, 250));
      await authHandleSignOut();
      navigateTo('home');
      toast.info('Du er logget ut.', { duration: 2000 });
    } catch (err) {
      devError('Logout failed:', err);
      toast.error('Kunne ikke logge ut. Prøv igjen.');
    } finally {
      setIsLoggingOut(false);
      setTimeout(() => {
        isSigningOutRef.current = false;
      }, 2000);
    }
  }, [authHandleSignOut, navigateTo, isLoggingOut]);

  const handleViewProfile = (userId: string) => {
    navigateTo('profile', userId);
  };

  const handleStartMessage = (userId: string) => {
    navigateTo('messages', undefined, userId);
  };

  // Announcement modal helpers
  const fetchAndShowAnnouncement = async (announcementId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/announcements/${announcementId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.announcement) {
          setCurrentAnnouncement(data.announcement);
          setAnnouncementModalOpen(true);
          
          // ✅ CRITICAL FIX: Mark as viewed with size limit (max 100 entries)
          addToLocalStorageArray('talentfunn_viewed_announcements', announcementId, 100);
        }
      }
    } catch (error) {
      devError('Error fetching announcement:', error);
    }
  };

  const checkActiveAnnouncement = async () => {
    if (!session) return;
    
    try {
      const response = await fetch(
        `${API_PUBLIC_BASE}/announcements/active`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.announcement && data.announcement.showToAll) {
          // Check if user has already viewed this announcement
          const viewedIds = JSON.parse(localStorage.getItem('talentfunn_viewed_announcements') || '[]');
          if (!viewedIds.includes(data.announcement.id)) {
            setCurrentAnnouncement(data.announcement);
            setAnnouncementModalOpen(true);
            
            // ✅ CRITICAL FIX: Mark as viewed with size limit (max 100 entries)
            addToLocalStorageArray('talentfunn_viewed_announcements', data.announcement.id, 100);
          }
        }
      }
    } catch (error) {
      devError('Error checking active announcement:', error);
    }
  };

  const normalizeNotificationDestination = useCallback((destination?: string | null): ViewType | null => {
    if (!destination) return null;
    if (destination === 'explore') return 'home';
    return destination as ViewType;
  }, []);

  const handleAnnouncementAction = () => {
    if (currentAnnouncement?.buttonDestination) {
      const destination = normalizeNotificationDestination(currentAnnouncement.buttonDestination);
      if (destination) {
        navigateTo(destination);
      }
    }
    setAnnouncementModalOpen(false);
  };

  const fetchUserProfile = useCallback(
    async (sessionOverride?: any): Promise<any> => {
      const guardedSetCurrentTermsVersion = (value: any) => {
        const nextValue =
          typeof value === 'function' ? value(currentTermsVersionRef.current) : value;
        setCurrentTermsVersion(nextValue);
        if (nextValue === CURRENT_TERMS_VERSION) {
          pendingTermsAcceptedVersionRef.current = null;
          pendingTermsAcceptanceUntilRef.current = 0;
          setNeedsTermsUpdate(false);
        }
      };
      const guardedSetNeedsTermsUpdate = (value: any) => {
        const nextValue =
          typeof value === 'function' ? value(needsTermsUpdateRef.current) : value;
        if (
          nextValue &&
          pendingTermsAcceptedVersionRef.current === CURRENT_TERMS_VERSION &&
          Date.now() < pendingTermsAcceptanceUntilRef.current
        ) {
          devLog('ℹ️ Suppressing transient terms modal reopen after local acceptance');
          return;
        }
        setNeedsTermsUpdate(nextValue);
      };
      return authFetchUserProfile(sessionOverride, {
        navigateTo,
        setCurrentView,
        setCurrentTermsVersion: guardedSetCurrentTermsVersion,
        setNeedsTermsUpdate: guardedSetNeedsTermsUpdate,
        setIsFirstLogin,
        handlingProfile401Ref,
      });
    },
    [
      authFetchUserProfile,
      navigateTo,
      setCurrentView,
      setCurrentTermsVersion,
      setNeedsTermsUpdate,
      setIsFirstLogin,
    ]
  );

  const requestUserProfileRefresh = useCallback(
    (
      reason: string,
      options?: {
        force?: boolean;
        sessionOverride?: any;
      }
    ) => {
      const force = options?.force === true;
      const activeUserId = options?.sessionOverride?.user?.id ?? sessionRef.current?.user?.id ?? null;
      const now = Date.now();
      const MIN_PROFILE_REFRESH_GAP_MS = 900;

      if (!force) {
        const elapsed = now - lastProfileRefreshAtRef.current;
        if (elapsed < MIN_PROFILE_REFRESH_GAP_MS) {
          devLog(`ℹ️ Skipping profile refresh (${reason}) - cooldown active (${elapsed}ms)`);
          return profileRefreshInFlightRef.current?.promise ?? Promise.resolve(null);
        }
      }

      if (profileRefreshInFlightRef.current) {
        if (profileRefreshInFlightRef.current.userId === activeUserId) {
          devLog(`ℹ️ Reusing in-flight profile refresh (${reason})`);
          return profileRefreshInFlightRef.current.promise;
        }
        devLog(`ℹ️ Not reusing in-flight refresh from another user (${reason})`);
      }

      const refreshPromise = fetchUserProfile(options?.sessionOverride)
        .finally(() => {
          if (profileRefreshInFlightRef.current?.promise === refreshPromise) {
            profileRefreshInFlightRef.current = null;
            lastProfileRefreshAtRef.current = Date.now();
          }
        });
      profileRefreshInFlightRef.current = {
        userId: activeUserId,
        promise: refreshPromise,
      };
      return refreshPromise;
    },
    [fetchUserProfile]
  );

  const shouldSkipAuthProfileRefresh = useCallback(
    (
      eventName: 'INIT_BOOTSTRAP' | 'INITIAL_SESSION' | 'SIGNED_IN' | 'TOKEN_REFRESHED',
      eventSession?: any
    ) => {
      const userId = eventSession?.user?.id;
      if (!userId) return false;
      const previous = lastAuthProfileRefreshRef.current;
      const now = Date.now();
      if (!previous || previous.userId !== userId) {
        return false;
      }

      if (
        eventName === 'INITIAL_SESSION' &&
        (previous.event === 'INIT_BOOTSTRAP' || previous.event === 'INITIAL_SESSION') &&
        now - previous.at < 5000
      ) {
        return true;
      }

      if (
        eventName === 'SIGNED_IN' &&
        (previous.event === 'SIGNED_IN' || previous.event === 'INITIAL_SESSION' || previous.event === 'INIT_BOOTSTRAP') &&
        now - previous.at < 2000
      ) {
        return true;
      }

      if (
        eventName === 'TOKEN_REFRESHED' &&
        previous.event === 'TOKEN_REFRESHED' &&
        now - previous.at < 1500
      ) {
        return true;
      }

      return false;
    },
    []
  );

  const handleProfileSettingsRefresh = useCallback(async () => {
    await requestUserProfileRefresh('settings:realtime-sync');
  }, [requestUserProfileRefresh]);

  const handleUnreadCountShouldRefresh = useCallback(() => {
    refreshUnreadCount('messages:opened');
  }, [refreshUnreadCount]);

  const handleGigsRefresh = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('talentfunn:gigs-refresh'));
    }
  }, []);

  const handleGigInterestsRefresh = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('talentfunn:gig-interests-refresh'));
    }
  }, []);

  // Phase 2: realtime user sync for profiles/subscriptions.
  useUserSync({
    session,
    enabled: true,
    onProfileRefresh: handleProfileSettingsRefresh,
    onSubscriptionRefresh: handleProfileSettingsRefresh,
    onBlockedUsersRefresh: handleProfileSettingsRefresh,
    onGigsRefresh: handleGigsRefresh,
    onGigInterestsRefresh: handleGigInterestsRefresh,
  });

  const handleTermsAccepted = () => {
    pendingTermsAcceptedVersionRef.current = CURRENT_TERMS_VERSION;
    pendingTermsAcceptanceUntilRef.current = Date.now() + 30000;
    setNeedsTermsUpdate(false);
    setCurrentTermsVersion(CURRENT_TERMS_VERSION);
    // Force a profile re-read after accept to confirm backend state and clear pending guard.
    void requestUserProfileRefresh('terms:accepted', { force: true });
  };

  // Handler for in-app notification navigation
  const handleNotificationNavigate = (
    destination: string,
    options?: {
      userId?: string;
      recipientId?: string;
      savedSearchId?: string;
      gigId?: string;
      gigTitle?: string;
    }
  ) => {
    const normalizedDestination = normalizeNotificationDestination(destination);
    if (!normalizedDestination) return;

    if (normalizedDestination === 'messages') {
      if (options?.recipientId) {
        navigateTo('messages', undefined, options.recipientId);
      } else {
        navigateTo('messages');
      }
      return;
    }

    if (normalizedDestination === 'profile') {
      if (options?.userId) {
        navigateTo('profile', options.userId);
      } else if (session) {
        navigateTo('profile', session.user.id);
      }
      return;
    }

    if (normalizedDestination === 'saved-searches') {
      setSelectedSavedSearchId(options?.savedSearchId || null);
      navigateTo('saved-searches');
      return;
    }

    if (normalizedDestination === 'gig-interests') {
      if (options?.gigId) {
        setInterestsViewGigId(options.gigId);
        if (options.gigTitle) setInterestsViewGigTitle(options.gigTitle);
      }
      navigateTo('gig-interests');
      return;
    }

    navigateTo(normalizedDestination);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-10 h-10 text-purple-600 animate-pulse" />
            <span className="text-4xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-semibold">
              Talentfunn
            </span>
          </div>
          <p className="text-purple-900 text-lg">Laster app...</p>
        </div>
      </div>
    );
  }

  // ⚠️ PILOT PHASE: Show access gate if user hasn't been granted access (REMOVE AT LAUNCH)
  if (checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-10 h-10 text-purple-600 animate-pulse" />
            <span className="text-4xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-semibold">
              Talentfunn
            </span>
          </div>
          <p className="text-purple-900 text-lg">Sjekker tilgang...</p>
        </div>
      </div>
    );
  }

  // Unified admin portal (#admin): supports both main-admin and sub-admin login.
  if (isAdminMode) {
    if (subAdminSession) {
      devLog('✅ Rendering SubAdminDashboard component');
      return (
        <SubAdminDashboard
          session={subAdminSession}
          accessToken={session?.access_token || ''}
          onLogout={() => {
            setSubAdminSession(null);
            localStorage.removeItem('sub_admin_session');
            setAdminLoginMode('sub');
          }}
        />
      );
    }

    // Show loading while verifying existing session is admin (avoids flashing main app)
    if (adminVerifying) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
          <div className="text-center">
            <Loader size="2xl" className="text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Verifiserer admin-tilgang...</p>
          </div>
        </div>
      );
    }

    if (!session || adminLoginMode === 'sub' || adminVerifyError) {
      if (adminLoginMode === 'sub') {
        devLog('✅ Rendering SubAdminLogin component');
        return (
          <SubAdminLogin
            onLoginSuccess={(session) => {
              setSubAdminSession(session);
            }}
            onSwitchToMainAdmin={() => setAdminLoginMode('main')}
            onBackToNormal={handleBackToNormalLogin}
          />
        );
      }

      devLog('✅ Rendering AdminLogin component');
      return (
        <AdminLogin
          onLogin={handleAdminLogin}
          onBackToNormal={handleBackToNormalLogin}
          onSwitchToSubAdmin={() => setAdminLoginMode('sub')}
          verifyError={adminVerifyError}
        />
      );
    }
  }

  devLog('🔍 Current state:', { isAdminMode, hasSession: !!session, hasAccess, checkingAccess, loading });

  // Show email verification page BEFORE AccessGate (verification links should work even during pilot phase)
  if (isEmailVerificationMode) {
    return (
      <EmailVerificationPage
        onVerified={async (isEmailChange) => {
          setIsEmailVerificationMode(false);
          
          // ✅ If email change, check if user is still logged in
          if (isEmailChange) {
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session) {
              // User is logged in - refresh profile and go to home
              void requestUserProfileRefresh('email-verification:onVerified', { sessionOverride: session });
              navigateTo('home');
            } else {
              // User logged out - go to auth so they can login with new email
              navigateTo('auth');
              toast.success('E-post bekreftet! Logg inn med din nye e-postadresse.', { duration: 5000 });
            }
          } else {
            // Signup - user needs to login
            navigateTo('auth');
          }
        }}
        onError={() => {
          setIsEmailVerificationMode(false);
          navigateTo('auth');
        }}
      />
    );
  }

  // Show AccessGate ONLY if admin has enabled it AND user hasn't passed it yet
  // ✅ NEW: No more hardcoded pilot/beta access - admin controls everything
  
  if (accessGateEnabled && !hasAccess) {
    return (
      <AccessGate 
        onAccessGranted={() => setHasAccess(true)}
        customTitle={accessGateTitle} // ✅ Always use admin-controlled values
        customDescription={accessGateDescription} // ✅ Always use admin-controlled values
        customButtonText={accessGateButtonText} // ✅ Always use admin-controlled values
        customIcon={accessGateIcon} // ✅ Always use admin-controlled values
        customTheme={accessGateTheme} // ✅ Always use admin-controlled values
        allowClose={accessGateAllowClose} // ✅ Always use admin-controlled values
        showButton={accessGateShowButton} // ✅ Always use admin-controlled values
        showInfo={accessGateShowInfo} // ✅ NEW - Toggle for info box
        customSubtitle={accessGateSubtitle} // ✅ NEW
        customInfoText={accessGateInfoText} // ✅ NEW
        customFooterText={accessGateFooterText} // ✅ NEW
        customContactEmail={accessGateContactEmail} // ✅ NEW
      />
    );
  }

  // Show auth page only for specific views that require login
  if (!session && (currentView === 'messages' || currentView === 'subscription')) {
    return (
      <AuthPage
        onAuthSuccess={async (newSession) => {
          setSession(newSession);
          let profile: any = null;
          try { profile = await fetchUserProfile(newSession); } catch (_) {}
          if (profile && !profile.isAdmin) navigateTo('profile', newSession.user.id);
          else if (!profile) navigateTo('home');
        }}
        onTermsClick={() => navigateTo('terms')}
        onFaqClick={() => navigateTo('faq')}
      />
    );
  }

  // 🛡️ BLOKKÉR ADMIN FRA Å BRUKE APPEN (må logge ut eller gå til admin-dashboard)
  // Unntak: Admin kan se brukerprofil når de navigerer dit med selectedUserId (f.eks. fra "Mest sette prosjekter")
  if (isAdmin && currentView !== 'admin' && !(currentView === 'profile' && selectedUserId)) {
    return (
      <AdminBlocker
        onGoToAdmin={() => navigateTo('admin')}
        onSignOut={handleSignOut}
      />
    );
  }

  // Mobile global chrome should stay mounted during modals so it can remain visible "behind"
  // (but non-interactive via body.talentfunn-modal-open CSS). Keep it hidden only for
  // true fullscreen views like in-chat messages and iOS PWA landscape safety overlay.
  const hideMobileGlobalChrome =
    (currentView === 'messages' && !!messageRecipientId) ||
    (isIosPwa && isIosPwaLandscape);

  return (
    <div className={`h-full min-h-0 flex flex-col overflow-hidden bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-orange-50 ${isIosPwa && hideMobileGlobalChrome ? 'ios-pwa-chat-fullscreen' : ''}`}>
      <ErrorBoundary>
      {isIosPwa && isIosPwaLandscape && (
        <div className="fixed inset-0 z-[100000] bg-white/95 backdrop-blur-sm flex items-center justify-center p-6 text-center">
          <div className="max-w-sm">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Roter tilbake til portrett</h2>
            <p className="text-gray-600">
              For best opplevelse i iOS-PWA bruker vi appen i portrettmodus.
            </p>
          </div>
        </div>
      )}
      <OfflineBanner />
      {/* Maintenance Mode Banner - always at top (app-shell) */}
      {maintenanceMode && (
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white py-3 px-4 text-center z-[60] shadow-lg">
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <AlertTriangle className="w-4 h-4 animate-pulse" />
            <span>{maintenanceMessage}</span>
            {isAdmin && (
              <span className="ml-3 text-xs bg-white/20 px-2 py-0.5 rounded">
                Du er admin – andre brukere ser dette nå
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Navigation - Desktop and Mobile (always at top, app-shell). Hidden on mobile when in fullscreen chat or fullscreen modal state. */}
      {currentView !== 'admin' && (
        <nav
          className={`flex-shrink-0 bg-white border-b border-purple-100 z-40 shadow-sm sm:block pwa-nav-top ${hideMobileGlobalChrome ? 'hidden xl:block' : ''}`}
          style={{ zIndex: 'var(--z-header)' }}
          aria-hidden={isModalFocusActive}
        >
          {/* Mobile header - centered logo (always clickable to go home) */}
          <div className="xl:hidden">
            <div className="max-w-7xl mx-auto px-4">
              <div className="relative flex justify-between items-center h-14">
                {/* Left: "Info" (when not logged in) or Notification Bell (when logged in) */}
                <div className="flex-1 min-w-10 flex items-center">
                  {session && isAdminCheckComplete ? (
                    <NotificationBell
                      unreadCount={unreadNotificationCount}
                      hasUnread={unreadNotificationCount > 0}
                      onClick={(anchorRect) => {
                        triggerAndroidHaptic('light');
                        setNotificationBellAnchor(anchorRect || null);
                        setIsNotificationCenterOpen(true);
                      }}
                    />
                  ) : !session ? (
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        setShowWhatIsTalentfunnModal(true);
                      }}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Info
                    </button>
                  ) : null}
                </div>
                
                {/* Centered logo */}
                <button
                  onClick={() => {
                    triggerAndroidHaptic('light');
                    navigateTo('home');
                  }}
                  className="absolute left-1/2 -translate-x-1/2 text-2xl whitespace-nowrap flex items-center gap-2"
                  aria-label="Gå til forsiden"
                >
                  <Sparkles className="w-7 h-7 text-purple-600" aria-hidden />
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                    Talentfunn
                  </span>
                </button>
                
                {/* Right: Settings gear (only when logged in) */}
                <div className="flex-1 min-w-10 flex items-center justify-end">
                  {session && isAdminCheckComplete ? (
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        navigateTo(currentView === 'settings' ? 'home' : 'settings');
                      }}
                      className="w-10 h-10 flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                      title="Innstillinger"
                    >
                      <Settings className="w-6 h-6" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          
          {/* Desktop header */}
          <div className="hidden xl:block">
            <div className="max-w-7xl mx-auto px-4 xl:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center gap-8 min-w-0 flex-1">
                  <button
                    onClick={() => {
                      triggerAndroidHaptic('light');
                      navigateTo('home');
                    }}
                    className="text-2xl whitespace-nowrap flex-shrink-0 flex items-center gap-2"
                  >
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                      Talentfunn
                    </span>
                  </button>
                  <div className={`flex gap-2 overflow-x-auto flex-1 min-w-0 ${!session ? 'justify-center' : ''}`} style={{ overflowY: 'visible' }}>
                    {!session && (
                      <button
                        onClick={() => {
                          triggerAndroidHaptic('light');
                          setShowWhatIsTalentfunnModal(true);
                        }}
                        className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-full transition-all text-base whitespace-nowrap font-medium"
                      >
                        Hva er Talentfunn?
                      </button>
                    )}
                    {session && isAdminCheckComplete && (
                      <>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            navigateTo('profile', session.user.id);
                          }}
                          className={`px-4 py-2 rounded-full transition-all text-base whitespace-nowrap ${
                            currentView === 'profile' && selectedUserId === session.user.id
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-purple-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>Min profil</span>
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            navigateTo('messages');
                          }}
                          className={`px-4 py-2 rounded-full transition-all relative text-base whitespace-nowrap overflow-visible ${
                            currentView === 'messages'
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-purple-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {unreadCount > 0 ? (
                              <span className="bg-red-500 text-white text-sm font-semibold px-2 py-0.5 rounded-full min-w-[24px] text-center">
                                {unreadCount}
                              </span>
                            ) : (
                              <MessageSquare className="w-4 h-4" />
                            )}
                            <span>Meldinger</span>
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            navigateTo('gigs');
                          }}
                          className={`px-4 py-2 rounded-full transition-all text-base whitespace-nowrap ${
                            (currentView === 'gigs' || currentView === 'gigs-coming')
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-purple-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            <span>Oppdrag</span>
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            navigateTo('subscription');
                          }}
                          className={`px-4 py-2 rounded-full transition-all text-base whitespace-nowrap ${
                            (currentView === 'subscription' || currentView === 'subscription-coming')
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-purple-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <Crown className="w-4 h-4" />
                            <span>Abonnement</span>
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            navigateTo('settings');
                          }}
                          className={`px-4 py-2 rounded-full transition-all text-base whitespace-nowrap ${
                            currentView === 'settings'
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-purple-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            <span>Innstillinger</span>
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Notification Bell (only when logged in) */}
                  {session && isAdminCheckComplete && (
                    <NotificationBell
                      unreadCount={unreadNotificationCount}
                      hasUnread={unreadNotificationCount > 0}
                      onClick={(anchorRect) => {
                        triggerAndroidHaptic('light');
                        setNotificationBellAnchor(anchorRect || null);
                        setIsNotificationCenterOpen(true);
                      }}
                    />
                  )}
                  
                  {session ? (
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('medium');
                        handleSignOut();
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-purple-50 rounded-full transition-all text-base whitespace-nowrap flex-shrink-0"
                    >
                      Logg ut
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        navigateTo('auth');
                      }}
                      className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-lg transition-all text-base whitespace-nowrap flex-shrink-0"
                    >
                      Opprett profil / Logg inn
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}
      
      {/* Mobile bottom navigation - iOS PWA: portaled to body; Android/desktop: fixed in app tree. Hidden in fullscreen chat/modal states. */}
      {session && !isAuthenticating && currentView !== 'admin' && currentView !== 'checkout' && !hideMobileGlobalChrome && (() => {
        // On iOS PWA and Android PWA on messages page: Don't set transform in inline style - let CSS handle it
        // EXCEPT: On Android, hide bottom nav bar when keyboard is open in chat
        const isMessagesPage = currentView === 'messages';
        const isAndroid = isAndroidPwa || (typeof document !== 'undefined' && /Android/.test(navigator.userAgent) && !document.documentElement.classList.contains('ios-pwa'));
        // ✅ FIX: Include iOS Safari (ios class) in CSS-only mode; fallback to userAgent so we always detect iOS even if classes are missing
        const isIOS = isIosPwa || (typeof document !== 'undefined' && document.documentElement.classList.contains('ios'))
          || /iPad|iPhone|iPod/.test(navigator.userAgent)
          || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const shouldUseCSSOnly = (isIOS && isMessagesPage) || (isAndroidPwa && isMessagesPage);
        const shouldHideNav = !shouldUseCSSOnly &&
          !['home', 'profile', 'messages', 'gigs', 'gigs-coming', 'settings', 'subscription', 'subscription-coming', 'my-gigs', 'my-interests', 'gig-interests', 'favorites', 'saved-searches', 'faq', 'privacy', 'terms', 'about'].includes(currentView);
        
        const navEl = (
          <nav
            className="xl:hidden fixed bottom-0 left-0 right-0 pwa-bottom-nav bg-white border-t border-purple-100 shadow-lg transition-transform duration-300"
            aria-hidden={isModalFocusActive}
            style={{
              zIndex: isAndroid && isMessagesPage ? '60' : 'var(--z-nav)',
              overscrollBehavior: 'none',
              ...(shouldUseCSSOnly ? {} : {
                transform: shouldHideNav
                  ? 'translateY(100%) translateZ(0)'
                  : 'translateY(0) translateZ(0)',
              }),
            }}
          >
            <div className={`grid grid-cols-4 gap-1 px-2 items-center shrink-0 ${isIosPwa ? 'h-12 min-h-12 py-0.5' : 'h-16 min-h-16 py-2'}`}>
              <button onClick={() => { triggerAndroidHaptic('light'); navigateTo('home'); }} className="flex flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-xl transition-colors min-h-0" aria-label="Talenter">
                <Search className={`w-6 h-6 shrink-0 transition-colors ${currentView === 'home' ? 'text-purple-600' : 'text-gray-600'}`} strokeWidth={currentView === 'home' ? 2.5 : 2} />
                <span className={`text-xs leading-tight truncate w-full text-center transition-colors ${currentView === 'home' ? 'font-semibold text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-gray-600'}`}>Talenter</span>
              </button>
              <button onClick={() => { triggerAndroidHaptic('light'); navigateTo('profile', session.user.id); }} className="flex flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-xl transition-colors min-h-0">
                <User className={`w-6 h-6 shrink-0 transition-colors ${currentView === 'profile' && selectedUserId === session.user.id ? 'text-purple-600' : 'text-gray-600'}`} strokeWidth={currentView === 'profile' && selectedUserId === session.user.id ? 2.5 : 2} />
                <span className={`text-xs leading-tight truncate w-full text-center transition-colors ${currentView === 'profile' && selectedUserId === session.user.id ? 'font-semibold text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-gray-600'}`}>Profil</span>
              </button>
              <button onClick={() => { triggerAndroidHaptic('light'); navigateTo('messages'); }} className="flex flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-xl transition-colors min-h-0 relative">
                <MessageSquare className={`w-6 h-6 shrink-0 transition-colors ${currentView === 'messages' ? 'text-purple-600' : 'text-gray-600'}`} strokeWidth={currentView === 'messages' ? 2.5 : 2} />
                <span className={`text-xs leading-tight truncate w-full text-center transition-colors ${currentView === 'messages' ? 'font-semibold text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-gray-600'}`}>Meldinger</span>
                {unreadCount > 0 && <span className="absolute top-1 right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-md">{unreadCount}</span>}
              </button>
              <button onClick={() => { triggerAndroidHaptic('light'); navigateTo('gigs'); }} className="flex flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-xl transition-colors min-h-0">
                <Briefcase className={`w-6 h-6 shrink-0 transition-colors ${(currentView === 'gigs' || currentView === 'gigs-coming') ? 'text-purple-600' : 'text-gray-600'}`} strokeWidth={(currentView === 'gigs' || currentView === 'gigs-coming') ? 2.5 : 2} />
                <span className={`text-xs leading-tight truncate w-full text-center transition-colors ${(currentView === 'gigs' || currentView === 'gigs-coming') ? 'font-semibold text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-gray-600'}`}>Oppdrag</span>
              </button>
            </div>
          </nav>
        );
        // iOS PWA: nav in app tree (no portal) so it can stay visible with body-scroll fix. iOS Safari: portal to body.
        // REVERT: For å portal nav på iOS PWA igjen, bruk: isIOS && typeof document !== 'undefined'
        const shouldPortalNav = isIOS && !isIosPwa && typeof document !== 'undefined';
        return shouldPortalNav
          ? createPortal(navEl, document.body, 'pwa-bottom-nav-portal')
          : navEl;
      })()}
      
      {/* Mobile login/register button when not logged in */}
      {!session && currentView !== 'admin' && ['home', 'profile'].includes(currentView) && (
        <div className="xl:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-purple-100 z-40 shadow-lg px-4 pt-3 pb-8" style={{ transform: 'translateZ(0)', overscrollBehaviorY: 'none', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}>
          <button
            onClick={() => {
              triggerAndroidHaptic('light');
              navigateTo('auth');
            }}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-lg transition-all text-base font-medium"
          >
            Opprett profil / Logg inn
          </button>
        </div>
      )}

      {/* Main content - ONLY scrollable area (app-shell) */}
      <main ref={mainRef} tabIndex={-1} className="flex-1 min-h-0 flex flex-col overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', color: 'rgba(10, 10, 10, 1)' }}>
        {/* Main persistent pages: on desktop (xl) single AnimatePresence for smooth transitions; on mobile all mounted with display toggle */}
        <div style={{ display: ANIMATED_VIEWS.has(currentView) ? 'none' : undefined, color: 'rgba(10, 10, 10, 1)' }} className="flex-1 min-h-0">
          {isDesktop ? (
            <>
            <AnimatePresence mode="wait">
              {mainContentView === 'home' && (
                <AnimatedPage key="home" direction={pageDirection}>
                  <div className="flex-1">
                    <HomePage
            key={session?.user?.id || 'logged-out'} // Force unmount on logout to clear modal/drawer state
            accessToken={session?.access_token || null}
            isDesktop={isDesktop}
            onViewProfile={handleViewProfile}
            onLoginRequired={() => navigateTo('auth')}
            userProfile={userProfile}
            initialSearchPreset={homeSearchPreset}
            onInitialSearchPresetApplied={() => setHomeSearchPreset(null)}
            blockedUserIds={blockedUserIds}
            onOpenMyGigs={() => navigateTo('my-gigs')}
            onOpenMyInterests={() => navigateTo('my-interests')}
            createGigModalTrigger={createGigModalTrigger}
            onClearCreateGigModalTrigger={() => setCreateGigModalTrigger(null)}
            onNavigateToSubscription={() => navigateTo('subscription')}
          />
                  </div>
                </AnimatedPage>
              )}
              {mainContentView === 'profile' && selectedUserId && (
                <AnimatedPage key="profile" direction={pageDirection}>
                  <div className="flex-1">
                    {blockedUserIds.includes(selectedUserId) && selectedUserId !== session?.user?.id ? (
                      <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
                        <div className="flex-1 overflow-y-auto ios-scroll">
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 pt-page-safe pb-16 xl:pt-5 xl:pb-12">
                            <div className="text-center py-12">
                              <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md mx-auto">
                                <Ban className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Profil ikke tilgjengelig</h2>
                                <p className="text-gray-600 mb-6">Du har blokkert denne brukeren og kan ikke se profilen.</p>
                                <button
                                  onClick={() => navigateTo('home')}
                                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
                                >
                                  Tilbake til søk
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <UserProfileView
                        key={`${session?.user?.id || 'logged-out'}-${selectedUserId}`}
                        userId={selectedUserId}
                        accessToken={session?.access_token || null}
                        currentUserId={session?.user?.id ?? undefined}
                        isOwnProfile={session ? selectedUserId === session.user.id : false}
                        currentUserProfile={userProfile}
                        isDesktop={isDesktop}
                        onStartMessage={session ? handleStartMessage : () => navigateTo('auth')}
                        onLoginRequired={() => navigateTo('auth')}
                        onViewFavorites={session ? () => navigateTo('favorites') : undefined}
                        onViewSavedSearches={session ? () => navigateTo('saved-searches') : undefined}
                        onSignOut={handleSignOut}
                        onNavigate={navigateTo}
                        onCreateGig={() => {
                          navigateTo('gigs');
                          setTimeout(() => setCreateGigModalTrigger({ openInView: 'gigs' }), 50);
                        }}
                        onProfileUpdate={() => requestUserProfileRefresh('profile:mutation', { force: true })}
                      />
                    )}
                  </div>
                </AnimatedPage>
              )}
              {mainContentView === 'messages' && session && (
                <AnimatedPage key="messages" direction={pageDirection}>
                  <div className="flex-1">
                    <MessagesPageEnhanced
                      key={session.user.id}
                      accessToken={session.access_token}
                      currentUserId={session.user.id}
                      initialRecipientId={messageRecipientId || undefined}
                      isVisible={true}
                      onBack={() => {
                        const origin = getHistoryFromView() || previousView;
                        if (origin === 'profile') {
                          navigateTo('profile', session.user.id);
                          return;
                        }
                        navigateTo('home');
                      }}
                      onViewProfile={(userId) => handleViewProfile(userId)}
                      onSelectConversation={(userId) => navigateTo('messages', undefined, userId)}
                      onBackToList={() => navigateTo('messages')}
                      userType={userProfile?.userType}
                      onUnreadCountShouldRefresh={handleUnreadCountShouldRefresh}
                    />
                  </div>
                </AnimatedPage>
              )}
              {currentView === 'gigs-coming' && (
                <AnimatedPage key="gigs-coming" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <GigsComingPage
                      onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
                    />
                  </div>
                </AnimatedPage>
              )}
              {mainContentView === 'gigs' && (
                <AnimatedPage key="gigs" direction={pageDirection}>
                  <div className="flex-1">
                    <GigsPage
                      key={session?.user?.id || 'logged-out'}
                      accessToken={session?.access_token || null}
                      currentUserId={session?.user?.id ?? undefined}
                      isDesktop={isDesktop}
                      onViewProfile={handleViewProfile}
                      onLoginRequired={() => navigateTo('auth')}
                      userProfile={userProfile}
                      onOpenMyInterests={() => navigateTo('my-interests')}
                      onOpenGigInterests={(gigId, gigTitle) => {
                        setInterestsViewGigId(gigId);
                        setInterestsViewGigTitle(gigTitle);
                        navigateTo('gig-interests');
                      }}
                      createGigModalTrigger={createGigModalTrigger}
                      onClearCreateGigModalTrigger={() => setCreateGigModalTrigger(null)}
                      onNavigateToSubscription={() => navigateTo('subscription')}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'my-gigs' && !isDesktop && (
                <AnimatedPage key="my-gigs" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <MyGigsPage
                      accessToken={session.access_token}
                      isVisible={true}
                      userType={userProfile?.userType || 'talent'}
                      onBack={() => {
                        const origin = getHistoryFromView() || previousView;
                        const target = origin === 'home' || origin === 'gigs' || origin === 'gigs-coming' ? origin : 'profile';
                        if (target === 'profile') {
                          navigateTo('profile', session.user.id);
                          return;
                        }
                        navigateTo(target);
                      }}
                      onViewInterests={(gigId, gigTitle) => {
                        setInterestsViewGigId(gigId);
                        setInterestsViewGigTitle(gigTitle);
                        navigateTo('gig-interests');
                      }}
                      onEditGig={async (gigId) => {
                        navigateTo('gigs');
                        setCreateGigModalTrigger({ gigIdToEdit: gigId, openInView: 'gigs' });
                      }}
                      onCreateGig={() => {
                        navigateTo('gigs');
                        setCreateGigModalTrigger({ openInView: 'gigs' });
                      }}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'my-interests' && !isDesktop && (
                <AnimatedPage key="my-interests" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <MyGigInterestsPage
                      accessToken={session.access_token}
                      isVisible={true}
                      onBack={() => {
                        const origin = getHistoryFromView() || previousView;
                        const target = origin === 'home' || origin === 'gigs' || origin === 'gigs-coming' ? origin : 'profile';
                        if (target === 'profile') {
                          navigateTo('profile', session.user.id);
                          return;
                        }
                        navigateTo(target);
                      }}
                      onViewGig={(gigId) => navigateTo('gigs')}
                      onExploreGigs={() => navigateTo('gigs')}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'gig-interests' && (
                <AnimatedPage key="gig-interests" direction={pageDirection}>
                  <div className="flex-1 min-h-0 flex flex-col">
                    <GigInterestsView
                      gigId={interestsViewGigId}
                      gigTitle={interestsViewGigTitle}
                      accessToken={session.access_token}
                      isVisible={true}
                      onViewProfile={handleViewProfile}
                      onStartConversation={(userId) => {
                        navigateTo('messages', undefined, userId);
                      }}
                      onBack={() => {
                        window.dispatchEvent(new Event('talentfunn:gigs-refresh'));
                        const origin = getHistoryFromView() || previousView;
                        if (origin === 'gigs') {
                          navigateTo('gigs');
                          return;
                        }
                        navigateTo('my-gigs');
                      }}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'favorites' && !isDesktop && (
                <AnimatedPage key="favorites" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <FavoritesPage
                      accessToken={session.access_token}
                      onBack={() => {
                        const origin = getHistoryFromView() || previousView;
                        if (origin === 'home') {
                          navigateTo('home');
                          return;
                        }
                        navigateTo('profile', session.user.id);
                      }}
                      onViewProfile={handleViewProfile}
                      isVisible={true}
                      onExploreTalents={() => navigateTo('home')}
                      onNavigateToSubscription={() => navigateTo('subscription')}
                    />
                  </div>
                </AnimatedPage>
              )}
              {currentView === 'subscription-coming' && (
                <AnimatedPage key="subscription-coming" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <SubscriptionComingPage
                      onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'subscription' && (
                <AnimatedPage key="subscription" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <SubscriptionPage
                      key={session.user.id}
                      accessToken={session.access_token}
                      userId={session.user.id}
                      onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
                      onUpgrade={() => {
                        void requestUserProfileRefresh('subscription:onUpgrade');
                        navigateTo('home');
                      }}
                      onCheckout={(planType, priceId) => {
                        devLog('🟢 onCheckout called in App.tsx');
                        setCheckoutPlanType(planType);
                        setCheckoutPriceId(priceId);
                        navigateTo('checkout');
                      }}
                      currentUserProfile={userProfile}
                      isVisible={true}
                      setSubscriptionType={setSubscriptionType}
                      navigateTo={navigateTo}
                      onOpenCancelModal={(subscriptionEndDate, currentPlan, onConfirm) => {
                        setCancelModalData({ subscriptionEndDate, currentPlan, onConfirm });
                        setShowCancelModal(true);
                      }}
                      onOpenReactivateModal={(currentPlan, onConfirm) => {
                        setReactivateModalData({ currentPlan, onConfirm });
                        setShowReactivateModal(true);
                      }}
                      refetchUserProfile={fetchUserProfile}
                      registerRefetchSubscription={(fn) => { subscriptionRefetchRef.current = fn; }}
                    />
                  </div>
                </AnimatedPage>
              )}
              {session && currentView === 'saved-searches' && !isDesktop && (
                <AnimatedPage key="saved-searches" direction={pageDirection}>
                  <div className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
                    <SavedSearchesPage
                      accessToken={session.access_token}
                      onBack={() => {
                        const origin = getHistoryFromView() || previousView;
                        if (origin === 'home') {
                          navigateTo('home');
                          return;
                        }
                        navigateTo('profile', session.user.id);
                      }}
                      onGoToSearch={(savedSearch) => {
                        const firstSearchTerm = (savedSearch?.searchTerms || [])
                          .map((term) => term?.toString().trim())
                          .find(Boolean) || '';
                        const counties = (savedSearch?.counties || [])
                          .map((county) => county?.toString().trim())
                          .filter(Boolean);
                        if (firstSearchTerm || counties.length > 0) {
                          setHomeSearchPreset({
                            id: `${Date.now()}`,
                            searchTerms: firstSearchTerm ? [firstSearchTerm] : [],
                            counties,
                            name: savedSearch?.name,
                          });
                        } else {
                          setHomeSearchPreset(null);
                        }
                        navigateTo('home');
                      }}
                      onViewProfile={handleViewProfile}
                      userProfile={userProfile}
                      initialSearchId={selectedSavedSearchId}
                      onClearInitialSearch={() => setSelectedSavedSearchId(null)}
                      isVisible={true}
                      onNavigateToSubscription={() => navigateTo('subscription')}
                    />
                  </div>
                </AnimatedPage>
              )}
            </AnimatePresence>
            {isSubPageModalActive &&
              createPortal(
                <DesktopSubPageModal isOpen onBack={handleSubPageModalBack}>
                  {currentView === 'favorites' && (
                    <FavoritesPage
                      accessToken={session!.access_token}
                      onBack={handleSubPageModalBack}
                      onViewProfile={handleViewProfile}
                      isVisible={true}
                      onExploreTalents={() => navigateTo('home')}
                      hideHeader
                      onNavigateToSubscription={() => navigateTo('subscription')}
                    />
                  )}
                  {currentView === 'saved-searches' && (
                    <SavedSearchesPage
                      accessToken={session!.access_token}
                      onBack={handleSubPageModalBack}
                      onGoToSearch={(savedSearch) => {
                        const firstSearchTerm = (savedSearch?.searchTerms || []).map((t) => t?.toString().trim()).find(Boolean) || '';
                        const counties = (savedSearch?.counties || []).map((c) => c?.toString().trim()).filter(Boolean);
                        if (firstSearchTerm || counties.length > 0) {
                          setHomeSearchPreset({ id: `${Date.now()}`, searchTerms: firstSearchTerm ? [firstSearchTerm] : [], counties, name: savedSearch?.name });
                        } else setHomeSearchPreset(null);
                        navigateTo('home');
                      }}
                      onViewProfile={handleViewProfile}
                      userProfile={userProfile}
                      initialSearchId={selectedSavedSearchId}
                      onClearInitialSearch={() => setSelectedSavedSearchId(null)}
                      isVisible={true}
                      hideHeader
                      onNavigateToSubscription={() => navigateTo('subscription')}
                    />
                  )}
                  {currentView === 'my-gigs' && (
                    <MyGigsPage
                      accessToken={session!.access_token}
                      isVisible={true}
                      userType={userProfile?.userType || 'talent'}
                      onBack={handleSubPageModalBack}
                      onViewInterests={(gigId, gigTitle) => {
                        setInterestsViewGigId(gigId);
                        setInterestsViewGigTitle(gigTitle);
                        navigateTo('gig-interests');
                      }}
                      onEditGig={async (gigId) => {
                        navigateTo('gigs');
                        setCreateGigModalTrigger({ gigIdToEdit: gigId, openInView: 'gigs' });
                      }}
                      onCreateGig={() => {
                        navigateTo('gigs');
                        setCreateGigModalTrigger({ openInView: 'gigs' });
                      }}
                      hideHeader
                    />
                  )}
                  {currentView === 'my-interests' && (
                    <MyGigInterestsPage
                      accessToken={session!.access_token}
                      isVisible={true}
                      onBack={handleSubPageModalBack}
                      onViewGig={() => navigateTo('gigs')}
                      onExploreGigs={() => navigateTo('gigs')}
                      hideHeader
                    />
                  )}
                </DesktopSubPageModal>,
                document.body
              )}
            </>
          ) : (
            <>
        {/* 4 BOTTOM NAV PAGES - ALLTID MOUNTET (instant switching!) */}
        {/* 🔒 SECURITY: key prop forces unmount/remount on user change to clear all local state */}
        <div style={{ display: currentView === 'home' ? 'block' : 'none' }} className="flex-1">
          <HomePage
            key={session?.user?.id || 'logged-out'} // Force unmount on logout to clear modal/drawer state
            accessToken={session?.access_token || null}
            isDesktop={isDesktop}
            onViewProfile={handleViewProfile}
            onLoginRequired={() => navigateTo('auth')}
            userProfile={userProfile}
            initialSearchPreset={homeSearchPreset}
            onInitialSearchPresetApplied={() => setHomeSearchPreset(null)}
            blockedUserIds={blockedUserIds}
            onOpenMyGigs={() => navigateTo('my-gigs')}
            onOpenMyInterests={() => navigateTo('my-interests')}
            createGigModalTrigger={createGigModalTrigger}
            onClearCreateGigModalTrigger={() => setCreateGigModalTrigger(null)}
            onNavigateToSubscription={() => navigateTo('subscription')}
          />
        </div>

        {selectedUserId && (
          <div style={{ display: currentView === 'profile' ? 'block' : 'none' }} className="flex-1">
            {/* Bulletproof: show loading when viewing own profile but userProfile not yet loaded (avoids white screen) */}
            {currentView === 'profile' && session && selectedUserId === session.user.id && !userProfile ? (
              <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
                <Loader size="xl" className="text-purple-600" />
                <p className="mt-3 text-purple-900">Laster profil...</p>
              </div>
            ) : blockedUserIds.includes(selectedUserId) && selectedUserId !== session?.user?.id ? (
              <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
                <div className="flex-1 overflow-y-auto ios-scroll">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 pt-page-safe pb-16 xl:pt-5 xl:pb-12">
                    <div className="text-center py-12">
                      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md mx-auto">
                        <Ban className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profil ikke tilgjengelig</h2>
                        <p className="text-gray-600 mb-6">Du har blokkert denne brukeren og kan ikke se profilen.</p>
                        <button
                          onClick={() => navigateTo('home')}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
                        >
                          Tilbake til søk
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <UserProfileView
                key={`${session?.user?.id || 'logged-out'}-${selectedUserId}`} // Force unmount on logout/user change
                userId={selectedUserId}
                accessToken={session?.access_token || null}
                currentUserId={session?.user?.id ?? undefined}
                isOwnProfile={session ? selectedUserId === session.user.id : false}
                currentUserProfile={userProfile}
                isDesktop={isDesktop}
                onStartMessage={session ? handleStartMessage : () => navigateTo('auth')}
                onLoginRequired={() => navigateTo('auth')}
                onViewFavorites={session ? () => navigateTo('favorites') : undefined}
                onViewSavedSearches={session ? () => navigateTo('saved-searches') : undefined}
                onSignOut={handleSignOut}
                onNavigate={navigateTo}
                onCreateGig={() => {
                  navigateTo('gigs');
                  // Defer trigger so GigsPage is visible and its useEffect runs after view switch
                  setTimeout(() => {
                    setCreateGigModalTrigger({ openInView: 'gigs' });
                  }, 50);
                }}
                onProfileUpdate={() => requestUserProfileRefresh('profile:mutation', { force: true })}
              />
            )}
          </div>
        )}

        {session && (
          <div style={{ display: currentView === 'messages' ? 'block' : 'none' }} className="flex-1">
            <MessagesPageEnhanced
              key={session.user.id} // Force unmount on logout to clear modal/drawer state
              accessToken={session.access_token}
              currentUserId={session.user.id}
              initialRecipientId={messageRecipientId || undefined}
              isVisible={currentView === 'messages'}
              onBack={() => {
                const origin = getHistoryFromView() || previousView;
                if (origin === 'profile') {
                  navigateTo('profile', session.user.id);
                  return;
                }
                navigateTo('home');
              }}
              onViewProfile={(userId) => handleViewProfile(userId)}
              onSelectConversation={(userId) => navigateTo('messages', undefined, userId)}
              onBackToList={() => navigateTo('messages')}
              userType={userProfile?.userType}
              onUnreadCountShouldRefresh={handleUnreadCountShouldRefresh}
            />
          </div>
        )}

        <div style={{ display: currentView === 'gigs-coming' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
          <GigsComingPage
            onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
          />
        </div>
        <div style={{ display: currentView === 'gigs' ? 'block' : 'none' }} className="flex-1">
          <GigsPage
            key={session?.user?.id || 'logged-out'}
            accessToken={session?.access_token || null}
            currentUserId={session?.user?.id ?? undefined}
            isDesktop={isDesktop}
            onViewProfile={handleViewProfile}
            onLoginRequired={() => navigateTo('auth')}
            userProfile={userProfile}
            onOpenMyInterests={() => navigateTo('my-interests')}
            onOpenGigInterests={(gigId, gigTitle) => {
              setInterestsViewGigId(gigId);
              setInterestsViewGigTitle(gigTitle);
              navigateTo('gig-interests');
            }}
            createGigModalTrigger={createGigModalTrigger}
            onClearCreateGigModalTrigger={() => setCreateGigModalTrigger(null)}
            onNavigateToSubscription={() => navigateTo('subscription')}
          />
        </div>

        {/* SUB-PAGES - ALWAYS MOUNTED (instant switching like main pages) */}
        {session && (
          <>
            <div style={{ display: currentView === 'my-gigs' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <MyGigsPage
                accessToken={session.access_token}
                isVisible={currentView === 'my-gigs'}
                userType={userProfile?.userType || 'talent'}
                onBack={() => {
                  const origin = getHistoryFromView() || previousView;
                  const target = origin === 'home' || origin === 'gigs' || origin === 'gigs-coming' ? origin : 'profile';
                  if (target === 'profile') {
                    navigateTo('profile', session.user.id);
                    return;
                  }
                  navigateTo(target);
                }}
                onViewInterests={(gigId, gigTitle) => {
                  setInterestsViewGigId(gigId);
                  setInterestsViewGigTitle(gigTitle);
                  navigateTo('gig-interests');
                }}
                onEditGig={async (gigId) => {
                  navigateTo('gigs');
                  setCreateGigModalTrigger({ gigIdToEdit: gigId, openInView: 'gigs' });
                }}
                onCreateGig={() => {
                  navigateTo('gigs');
                  setCreateGigModalTrigger({ openInView: 'gigs' });
                }}
              />
            </div>

            <div style={{ display: currentView === 'my-interests' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <MyGigInterestsPage
                accessToken={session.access_token}
                isVisible={currentView === 'my-interests'}
                onBack={() => {
                  const origin = getHistoryFromView() || previousView;
                  const target = origin === 'home' || origin === 'gigs' || origin === 'gigs-coming' ? origin : 'profile';
                  if (target === 'profile') {
                    navigateTo('profile', session.user.id);
                    return;
                  }
                  navigateTo(target);
                }}
                onViewGig={(gigId) => {
                  // Navigate users back to gigs list context for gig exploration.
                  navigateTo('gigs');
                }}
                onExploreGigs={() => navigateTo('gigs')}
              />
            </div>

            <div style={{ display: currentView === 'gig-interests' ? 'block' : 'none' }} className="flex-1 min-h-0 flex flex-col">
              <GigInterestsView
                gigId={interestsViewGigId}
                gigTitle={interestsViewGigTitle}
                accessToken={session.access_token}
                isVisible={currentView === 'gig-interests'}
                onViewProfile={handleViewProfile}
                onStartConversation={(userId) => {
                  navigateTo('messages', undefined, userId);
                }}
                onBack={() => {
                  window.dispatchEvent(new Event('talentfunn:gigs-refresh'));
                  const origin = getHistoryFromView() || previousView;
                  if (origin === 'gigs') {
                    navigateTo('gigs');
                    return;
                  }
                  navigateTo('my-gigs');
                }}
              />
            </div>

            <div style={{ display: currentView === 'favorites' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <FavoritesPage
                accessToken={session.access_token}
                onBack={() => {
                  const origin = getHistoryFromView() || previousView;
                  if (origin === 'home') {
                    navigateTo('home');
                    return;
                  }
                  navigateTo('profile', session.user.id);
                }}
                onViewProfile={handleViewProfile}
                isVisible={currentView === 'favorites'}
                onExploreTalents={() => navigateTo('home')}
                onNavigateToSubscription={() => navigateTo('subscription')}
              />
            </div>

            <div style={{ display: currentView === 'subscription-coming' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <SubscriptionComingPage
                onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
              />
            </div>
            <div style={{ display: currentView === 'subscription' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <SubscriptionPage
                key={session.user.id}
                accessToken={session.access_token}
                userId={session.user.id}
                onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
                onUpgrade={() => {
                  void requestUserProfileRefresh('subscription:onUpgrade');
                  navigateTo('home');
                }}
                onCheckout={(planType, priceId) => {
                  devLog('🟢 onCheckout called in App.tsx');
                  devLog('🟢 planType:', planType, 'priceId:', priceId);
                  if (priceId === '__UPDATE_PAYMENT_DETAILS__') {
                    checkoutReturnToViewRef.current = 'subscription';
                  }
                  setCheckoutPlanType(planType);
                  setCheckoutPriceId(priceId);
                  devLog('🟢 State set, navigating to checkout...');
                  navigateTo('checkout');
                }}
                onOpenUpdatePayment={(returnTo, planType) => {
                  checkoutReturnToViewRef.current = returnTo;
                  setCheckoutPlanType(planType ?? 'talent_plus');
                  setCheckoutPriceId('__UPDATE_PAYMENT_DETAILS__');
                  navigateTo('checkout');
                }}
                currentUserProfile={userProfile}
                isVisible={currentView === 'subscription'}
                setSubscriptionType={setSubscriptionType}
                navigateTo={navigateTo}
                onOpenCancelModal={(subscriptionEndDate, currentPlan, onConfirm) => {
                  devLog('🟢 onOpenCancelModal called in App.tsx', { currentPlan });
                  setCancelModalData({ subscriptionEndDate, currentPlan, onConfirm });
                  setShowCancelModal(true);
                }}
                onOpenReactivateModal={(currentPlan, onConfirm) => {
                  devLog('🟢 onOpenReactivateModal called in App.tsx', { currentPlan });
                  setReactivateModalData({ currentPlan, onConfirm });
                  setShowReactivateModal(true);
                }}
                refetchUserProfile={fetchUserProfile}
                registerRefetchSubscription={(fn) => { subscriptionRefetchRef.current = fn; }}
              />
            </div>

            <div style={{ display: currentView === 'saved-searches' ? 'block' : 'none' }} className="flex-when-visible flex-1 xl:min-h-0 flex flex-col">
              <SavedSearchesPage
                accessToken={session.access_token}
                onBack={() => {
                  const origin = getHistoryFromView() || previousView;
                  if (origin === 'home') {
                    navigateTo('home');
                    return;
                  }
                  navigateTo('profile', session.user.id);
                }}
                onGoToSearch={(savedSearch) => {
                  const firstSearchTerm = (savedSearch?.searchTerms || [])
                    .map((term) => term?.toString().trim())
                    .find(Boolean) || '';
                  const counties = (savedSearch?.counties || [])
                    .map((county) => county?.toString().trim())
                    .filter(Boolean);

                  if (firstSearchTerm || counties.length > 0) {
                    setHomeSearchPreset({
                      id: `${Date.now()}`,
                      searchTerms: firstSearchTerm ? [firstSearchTerm] : [],
                      counties,
                      name: savedSearch?.name,
                    });
                  } else {
                    setHomeSearchPreset(null);
                  }

                  navigateTo('home');
                }}
                onViewProfile={handleViewProfile}
                userProfile={userProfile}
                initialSearchId={selectedSavedSearchId}
                onClearInitialSearch={() => setSelectedSavedSearchId(null)}
                isVisible={currentView === 'saved-searches'}
                onNavigateToSubscription={() => navigateTo('subscription')}
              />
            </div>
          </>
        )}
            </>
          )}
        </div>

        {/* ALLE ANDRE SIDER - MODAL/UNDERSIDER MED ANIMASJON */}
        {/* Hidden when a persistent page/sub-page is active so it doesn't steal flex space */}
        <div style={{ display: ANIMATED_VIEWS.has(currentView) ? undefined : 'none' }} className="flex-1 overflow-y-auto relative animated-pages-container-scroll">
          <AnimatePresence mode="wait">
            {currentView === 'auth' && (
              <AnimatedPage key="auth" direction={pageDirection}>
                <AuthPage 
                  onAuthSuccess={async (newSession) => {
                    setIsAuthenticating(true);
                    setSession(newSession);
                    try {
                      // Bulletproof: await profile, but never allow a hung request to keep UI in limbo.
                      const timeoutMs = 12000;
                      const profile: any = await Promise.race([
                        fetchUserProfile(newSession),
                        new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
                      ]);

                      if (profile && !profile.isAdmin) {
                        navigateTo('profile', newSession.user.id);
                        return;
                      }

                      // Fallback: user is logged in but profile isn't ready yet.
                      navigateTo('home');
                      toast.info('Innlogging fullført. Du kan gå til Min profil når den er lastet.', { duration: 4000 });

                      // Background retries to escape "Laster profil..." limbo on flaky networks.
                      // requestUserProfileRefresh dedupes in-flight calls.
                      setTimeout(() => {
                        if (sessionRef.current?.user?.id === newSession.user.id && !userProfileRef.current) {
                          void requestUserProfileRefresh('auth:post-login-retry-2s', { force: true });
                        }
                      }, 2000);
                      setTimeout(() => {
                        if (sessionRef.current?.user?.id === newSession.user.id && !userProfileRef.current) {
                          void requestUserProfileRefresh('auth:post-login-retry-5s', { force: true });
                        }
                      }, 5000);
                    } catch (err) {
                      devError('Login onAuthSuccess flow failed:', err);
                      navigateTo('home');
                    } finally {
                      setIsAuthenticating(false);
                    }
                  }} 
                  onForgotPassword={() => navigateTo('forgot-password')}
                  onTermsClick={() => navigateTo('terms')}
                  onFaqClick={() => navigateTo('faq')}
                  onBack={() => navigateTo('home')}
                />
              </AnimatedPage>
            )}
            {currentView === 'checkout' && session && checkoutPlanType && checkoutPriceId && (
              <AnimatedPage key="checkout" direction={pageDirection}>
                <StripeCheckout
                  accessToken={session.access_token}
                  userId={session.user.id}
                  onBack={() => {
                    if (window.history.length > 1) {
                      window.history.back();
                    } else {
                      navigateTo('subscription');
                    }
                  }}
                  planType={checkoutPlanType}
                  priceId={checkoutPriceId}
                  userType={userProfile?.userType} 
                  onViewTerms={() => navigateTo('terms')}
                  onViewWithdrawalForm={() => navigateTo('angrerett')}
                  onSuccess={(planType) => {
                    devLog('✅ Payment success callback received! Plan:', planType);
                    const returnTo = checkoutReturnToViewRef.current;
                    checkoutReturnToViewRef.current = null;
                    setCheckoutPlanType(null);
                    setCheckoutPriceId('');

                    void requestUserProfileRefresh('checkout:success:initial', { force: true });
                    setTimeout(() => void requestUserProfileRefresh('checkout:success:retry-2s'), 2000);
                    setTimeout(() => void requestUserProfileRefresh('checkout:success:retry-5s'), 5000);

                    if (returnTo === 'subscription' || returnTo === 'settings') {
                      navigateTo(returnTo);
                      return;
                    }
                    const mappedType = planType === 'talent_plus' ? 'plus' : 
                                       planType === 'arbeidsgiver' ? 'employer' : 
                                       'employer_plus';
                    setSubscriptionType(mappedType);
                    navigateTo('thank-you');
                  }}
                />
              </AnimatedPage>
            )}
            {currentView === 'admin' && (
              <AnimatedPage key="admin" direction={pageDirection}>
                {!isAdminCheckComplete ? (
                  <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
                    <div className="text-center">
                      <Loader size="2xl" className="text-purple-600 mx-auto mb-4" />
                      <p className="text-gray-600">Laster admin dashboard...</p>
                    </div>
                  </div>
                ) : !session || !isAdmin ? (
                  <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4">
                    <div className="bg-white rounded-2xl shadow-lg border border-purple-100 max-w-md w-full p-6 text-center">
                      <p className="text-gray-900 font-semibold mb-2">Ingen tilgang til admin</p>
                      <p className="text-sm text-gray-600 mb-5">
                        Denne siden krever admin-rettigheter.
                      </p>
                      <button
                        onClick={() => navigateTo('home')}
                        className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:shadow-lg transition-all"
                      >
                        Til forsiden
                      </button>
                    </div>
                  </div>
                ) : (
                  <AdminDashboard
                    accessToken={session.access_token}
                    onBack={() => navigateTo('home')}
                  />
                )}
              </AnimatedPage>
            )}
            {currentView === 'privacy' && (
              <AnimatedPage key="privacy" direction={pageDirection}>
                <PrivacyPolicyPage onBack={() => {
                  // Go back to previous view, or home as fallback
                  navigateTo(previousView || 'home');
                }} />
              </AnimatedPage>
            )}
            {currentView === 'terms' && (
              <AnimatedPage key="terms" direction={pageDirection}>
                <TermsOfServicePage
                  userType={userProfile?.userType === 'employer' ? 'Arbeidsgiver' : 'Talent'}
                  onBack={() => {
                    // Go back to previous view, or home as fallback
                    navigateTo(previousView || 'home');
                  }}
                  onWithdrawalForm={() => navigateTo('angrerett')}
                  isLoggedIn={!!session}
                  onLoginRequired={() => navigateTo('auth')}
                  onNavigateToPrivacy={() => navigateTo('privacy')}
                />
              </AnimatedPage>
            )}
            {currentView === 'forgot-password' && (
              <AnimatedPage key="forgot-password" direction={pageDirection}>
                <ForgotPasswordPage 
                  onBack={() => navigateTo('auth')} 
                  onLoginRedirect={() => navigateTo('auth')}
                />
              </AnimatedPage>
            )}
            {currentView === 'unsubscribe' && (
              <AnimatedPage key="unsubscribe" direction={pageDirection}>
                <UnsubscribePage onNavigate={navigateTo} />
              </AnimatedPage>
            )}
            {currentView === 'settings' && (
              <AnimatedPage key="settings" direction={pageDirection}>
                <SettingsPage 
                  onNavigate={navigateTo}
                  currentSettings={{
                    hideOnlineStatus: userProfile?.hideOnlineStatus || false,
                  }}
                  onSignOut={handleSignOut}
                  onProfileUpdate={() => requestUserProfileRefresh('settings:onProfileUpdate', { force: true })}
                  onOpenUpdatePayment={() => {
                    checkoutReturnToViewRef.current = 'settings';
                    const sub = userProfile?.subscription;
                    const planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus' =
                      sub === 'employer_plus' ? 'arbeidsgiver_plus'
                      : sub === 'employer' ? 'arbeidsgiver'
                      : 'talent_plus';
                    setCheckoutPlanType(planType);
                    setCheckoutPriceId('__UPDATE_PAYMENT_DETAILS__');
                    navigateTo('checkout');
                  }}
                />
              </AnimatedPage>
            )}
            {currentView === 'faq' && (
              <AnimatedPage key="faq" direction={pageDirection}>
                <FAQPage onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')} />
              </AnimatedPage>
            )}
            {currentView === 'thank-you' && subscriptionType && (
              <AnimatedPage key="thank-you" direction={pageDirection}>
                <ThankYouPage 
                  subscriptionType={subscriptionType}
                  onContinue={() => {
                    setSubscriptionType(null);
                    // CRITICAL: Refresh user profile one final time before going to subscription page
                    devLog('🔄 Final profile refresh before returning to subscription page...');
                    void requestUserProfileRefresh('thank-you:continue');
                    navigateTo('subscription');
                  }} 
                />
              </AnimatedPage>
            )}
            {currentView === 'angrerett' && (
              <AnimatedPage key="angrerett" direction={pageDirection}>
                <WithdrawalFormPage
                  onBack={() => navigateTo(getHistoryFromView() || previousView || 'home')}
                />
              </AnimatedPage>
            )}
            {currentView === 'about' && (
              <AnimatedPage key="about" direction={pageDirection}>
                <AboutPage onBack={() => navigateTo(previousView || 'home')} />
              </AnimatedPage>
            )}
          </AnimatePresence>
        </div>

        {/* Footer - always at bottom of content */}
        <Footer />
      </main>

      {/* Terms Update Modal - portalZIndex so it appears above admin dashboard (z-[10000]) */}
      {needsTermsUpdate && session && (
        <TermsUpdateModal
          accessToken={session.access_token}
          currentVersion={currentTermsVersion}
          newVersion={CURRENT_TERMS_VERSION}
          onAccept={handleTermsAccepted}
          portalZIndex={currentView === 'admin' ? 10001 : undefined}
        />
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Cookie Consent */}
      <CookieConsent />

      {/* Announcement Modal */}
      <AnnouncementModal
        isOpen={announcementModalOpen}
        announcement={currentAnnouncement}
        onClose={() => setAnnouncementModalOpen(false)}
        onActionClick={handleAnnouncementAction}
      />

      {/* What is Talentfunn? Modal (when not logged in) */}
      <WhatIsTalentfunnModal
        isOpen={showWhatIsTalentfunnModal}
        onClose={() => setShowWhatIsTalentfunnModal(false)}
        onNavigateToFaq={() => navigateTo('faq')}
        onNavigateToAuth={() => navigateTo('auth')}
      />

      {/* Cancel Subscription Modal */}
      {showCancelModal && cancelModalData && createPortal(
        <div 
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
          style={{ zIndex: 'var(--z-modal)' }}
          onClick={() => {
            setShowCancelModal(false);
            setCancelModalData(null);
          }}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-3">Avslutt abonnement?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Du vil beholde tilgang til {
                cancelModalData.subscriptionEndDate 
                  ? new Date(cancelModalData.subscriptionEndDate).toLocaleDateString('nb-NO', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })
                  : 'slutten av perioden'
              }. Abonnementet fornyes ikke automatisk.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  devLog('🟢 Avbryt clicked in App.tsx');
                  setShowCancelModal(false);
                  setCancelModalData(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  devLog('🟢 Avslutt clicked in App.tsx');
                  
                  setModalActionLoading(true);
                  try {
                    // Cancel subscription first
                    await cancelModalData.onConfirm();
                    
                    // ✅ FIX: Calculate days left from subscriptionEndDate (ISO string from backend)
                    const endDate = cancelModalData.subscriptionEndDate;
                    let daysLeft = 30; // ✅ Fallback for new subscriptions without currentPeriodEnd yet
                    
                    if (endDate) {
                      try {
                        // Parse ISO string from backend (e.g. "2026-03-06T12:34:56.000Z")
                        const endDateTime = new Date(endDate).getTime();
                        const nowTime = new Date().getTime();
                        const calculatedDays = Math.ceil((endDateTime - nowTime) / (1000 * 60 * 60 * 24));
                        
                        // Only use calculated days if valid (> 0)
                        if (!isNaN(calculatedDays) && calculatedDays > 0) {
                          daysLeft = calculatedDays;
                        }
                        
                        devLog('📅 Days calculation:', { endDate, calculatedDays, daysLeft });
                      } catch (e) {
                        devLog('⚠️ Could not parse subscriptionEndDate, using fallback:', endDate);
                      }
                    } else {
                      devLog('⚠️ No subscriptionEndDate provided, using fallback 30 days');
                    }
                    
                    // Close cancel modal and show confirmation
                    setShowCancelModal(false);
                    setCancelModalData(null);
                    setCancellationDaysLeft(daysLeft);
                    setShowCancellationConfirmation(true);
                  } finally {
                    setModalActionLoading(false);
                  }
                }}
                disabled={modalActionLoading}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  modalActionLoading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {modalActionLoading ? (
                  <>
                    <Loader size="sm" className="text-white" />
                    Avslutter...
                  </>
                ) : (
                  'Avslutt'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ✅ NEW: Cancellation Confirmation Modal */}
      {showCancellationConfirmation && createPortal(
        <div 
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
          style={{ zIndex: 'var(--z-modal)' }}
          onClick={() => {
            subscriptionRefetchRef.current?.();
            setShowCancellationConfirmation(false);
          }}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center mb-6">
              <div className="bg-green-100 text-green-600 rounded-full p-3 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Abonnement avsluttet</h3>
              <p className="text-gray-600">
                Du har <span className="font-bold text-gray-900">{cancellationDaysLeft} {cancellationDaysLeft === 1 ? 'dag' : 'dager'}</span> igjen av abonnementet.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Du vil beholde tilgang til alle funksjoner til perioden er slutt.
              </p>
            </div>
            <button
              onClick={() => {
                subscriptionRefetchRef.current?.();
                setShowCancellationConfirmation(false);
              }}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Lukk
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ✅ NEW: Reactivate Modal (before reactivation) */}
      {showReactivateModal && reactivateModalData && createPortal(
        <div 
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
          style={{ zIndex: 'var(--z-modal)' }}
          onClick={() => setShowReactivateModal(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-3">Reaktiver abonnement?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Abonnementet vil fornyes automatisk ved neste periode.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  devLog('🟢 Avbryt reaktivering clicked');
                  setShowReactivateModal(false);
                  setReactivateModalData(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  devLog('🟢 Reaktiver clicked in App.tsx');
                  
                  setModalActionLoading(true);
                  try {
                    // Reactivate subscription
                    await reactivateModalData.onConfirm();
                    
                    // Close reactivate modal
                    setShowReactivateModal(false);
                    
                    // Clear data
                    setReactivateModalData(null);
                    
                    // Show success confirmation
                    setShowReactivationConfirmation(true);
                  } finally {
                    setModalActionLoading(false);
                  }
                }}
                disabled={modalActionLoading}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  modalActionLoading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {modalActionLoading ? (
                  <>
                    <Loader size="sm" className="text-white" />
                    Reaktiverer...
                  </>
                ) : (
                  'Reaktiver'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ✅ NEW: Reactivation Confirmation Modal (after reactivation) */}
      {showReactivationConfirmation && createPortal(
        <div 
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
          style={{ zIndex: 'var(--z-modal)' }}
          onClick={() => setShowReactivationConfirmation(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center mb-6">
              <div className="bg-green-100 text-green-600 rounded-full p-3 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Abonnement reaktivert!</h3>
              <p className="text-gray-600">
                Abonnementet vil fornyes automatisk ved neste periode.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Du har fortsatt tilgang til alle funksjoner.
              </p>
            </div>
            <button
              onClick={() => setShowReactivationConfirmation(false)}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Lukk
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Notification Center Modal */}
      {session && (
        <NotificationCenter
          isOpen={isNotificationCenterOpen}
          onClose={() => {
            setIsNotificationCenterOpen(false);
            setNotificationBellAnchor(null);
          }}
          accessToken={session.access_token}
          onNavigate={handleNotificationNavigate}
          onNotificationRead={() => refreshUnreadNotificationCount('notifications:read')}
          onProfileRefresh={fetchUserProfile}
          anchorRect={notificationBellAnchor}
        />
      )}

      </ErrorBoundary>
      {/* Toast Notifications - top center; on iOS PWA use larger top offset so toasts sit below camera/notch */}
      <Toaster 
        position="top-center"
        richColors 
        mobileOffset={isIosPwa ? 72 : undefined}
        toastOptions={{ 
          duration: 4000,
          className: 'toast-custom'
        }} 
      />
      
      {/* Update Banner - portaled to document.body so it always appears above all modals (PostDetailView, etc.) */}
      {showUpdateBanner && createPortal(
        <div className="fixed bottom-20 left-0 right-0 flex justify-center px-4 pb-safe" style={{ zIndex: 100002 }}>
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-2xl shadow-2xl max-w-md w-full flex flex-col gap-3 animate-slideUp">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-semibold">Ny oppdatering tilgjengelig!</p>
                <p className="text-sm text-white/90">Klikk for å oppdatere</p>
              </div>
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    devLog('ℹ️ User dismissed update banner (Senere)');
                    setShowUpdateBanner(false);
                  }}
                  className="px-4 py-2 rounded-lg font-medium border border-white/60 text-white hover:bg-white/10 transition-colors active:scale-95 whitespace-nowrap"
                >
                  Senere
                </button>
                <button
                  onClick={async () => {
                  if (!updateWaiting || isUpdating) return; // Prevent double-clicks
                  
                  setIsUpdating(true);
                  // 🔒 Flag update in progress so auth handlers ignore AbortErrors during SW swap
                  markSwUpdating();
                  devLog('🔄 User clicked update button - starting update process...');
                  
                  try {
                    const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
                    
                    // Step 1: Explicitly save session before triggering update
                    devLog('💾 Step 1: Saving session before update...');
                    try {
                      const currentSession = await supabase.auth.getSession();
                      if (currentSession.data.session) {
                        devLog('✅ Session exists before update:', currentSession.data.session.user.email);
                        
                        // Explicitly refresh session to ensure it's saved to localStorage
                        // This is critical on Android where localStorage writes can be slow
                        devLog('🔄 Refreshing session to ensure it\'s saved...');
                        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
                        
                        if (refreshError) {
                          devError('⚠️ Failed to refresh session before update:', refreshError);
                        } else if (refreshData.session) {
                          devLog('✅ Session refreshed and saved before update');
                        }
                        
                        // On Android, give extra time for localStorage to be written
                        if (isAndroid) {
                          devLog('⏳ Waiting for Android localStorage write...');
                          await new Promise(resolve => setTimeout(resolve, 500));
                          
                          // Verify session is still there after wait
                          const verifySession = await supabase.auth.getSession();
                          if (!verifySession.data.session) {
                            devError('❌ Session lost during save process!');
                            toast.error('Kunne ikke lagre innlogging. Prøv igjen.');
                            clearSwUpdateGuard();
                            setIsUpdating(false);
                            return;
                          }
                          devLog('✅ Session verified after save');
                        }
                        
                        // 🔒 Backup session to a separate key that SIGNED_OUT handler won't touch
                        const authBackup = localStorage.getItem('talentfunn-auth');
                        if (authBackup) {
                          localStorage.setItem('talentfunn-auth-backup', authBackup);
                          devLog('✅ Session backed up to talentfunn-auth-backup');
                        }
                      } else {
                        devLog('⚠️ No session found before update - user may need to login again');
                      }
                    } catch (err) {
                      devError('⚠️ Error saving session before update:', err);
                    }
                    
                    // Step 2: Check if there are multiple waiting service workers
                    devLog('🔍 Step 2: Checking for multiple waiting service workers...');
                    const allRegistrations = await navigator.serviceWorker.getRegistrations();
                    let hasMultipleWaiting = false;
                    const waitingWorkers: ServiceWorker[] = [];
                    
                    for (const reg of allRegistrations) {
                      if (reg.waiting && reg.waiting !== updateWaiting) {
                        devLog('⚠️ Found additional waiting service worker');
                        hasMultipleWaiting = true;
                        waitingWorkers.push(reg.waiting);
                      }
                    }
                    
                    // Step 3: Skip waiting on all workers
                    devLog('⏭️ Step 3: Skipping waiting on all service workers...');
                    for (const worker of waitingWorkers) {
                      worker.postMessage({ type: 'SKIP_WAITING' });
                    }
                    updateWaiting.postMessage({ type: 'SKIP_WAITING' });
                    
                    // ✅ CRITICAL: Keep update guard active to prevent update banner loops
                    markSwUpdating();
                    
                    // Step 4: Wait for skipWaiting to complete before reload
                    if (hasMultipleWaiting) {
                      devLog('🚀 Multiple updates detected - waiting for all to skip...');
                      toast.info('Oppdaterer til nyeste versjon...', { duration: 3000 });
                      
                      // Wait for multiple updates to process (same timing for all platforms)
                      const waitTime = 1000; // 1s for all platforms
                      await new Promise(resolve => setTimeout(resolve, waitTime));
                      
                      devLog('🔄 Reloading after multiple updates...');
                      window.location.reload();
                    } else {
                      // Single update - wait for controllerchange event instead of setTimeout
                      devLog('⏳ Waiting for service worker to activate...');
                      toast.info('Oppdaterer...', { duration: 2000 });
                      // controllerchange event will handle the reload
                    }
                  } catch (error) {
                    devError('❌ Error during update process:', error);
                    toast.error('Noe gikk galt under oppdatering. Prøv igjen.');
                    clearSwUpdateGuard();
                    setIsUpdating(false);
                  }
                  
                  setShowUpdateBanner(false);
                }}
                className="ml-4 bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-purple-50 transition-colors active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[7rem]"
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader size="sm" className="text-purple-600" />
                ) : (
                  <span>Oppdater</span>
                )}
                </button>
              </div>
            </div>
            <p className="text-xs text-white/80">Du forblir innlogget etter oppdatering</p>
          </div>
        </div>,
        document.body
      )}

      {/* Logout overlay: fade out current view, then fade in to home (user-initiated only) */}
      <AnimatePresence mode="wait">
        {isLoggingOut && (
          <motion.div
            key="logout-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="fixed inset-0 bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-orange-50 pointer-events-none z-[50000]"
            aria-hidden
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <NavProvider>
          <WelcomeModalProvider>
            <AppContent />
          </WelcomeModalProvider>
        </NavProvider>
      </ChatProvider>
    </AuthProvider>
  );
}
